import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Heart, Plus, Target, Clock, Users, Check, X, Wallet, DollarSign, ArrowLeft, Star, Rocket, Share2, Megaphone, Copy, QrCode, ExternalLink, Download, Pencil, Trash2, Image, Loader2, Upload, AlertTriangle, MoreHorizontal, Trophy, Flame, ThumbsUp, BadgeCheck, TrendingUp, CalendarPlus, Search, MessageSquarePlus, LinkIcon, Camera, Video, ChevronDown, ChevronUp, CircleCheckBig, History, ShieldCheck, Construction, CheckCircle2, Award } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { uploadImage } from '../lib/imageUpload';
import { useNostrQuery, useNostrProfile, getCachedProfile } from '../hooks/useNostrQuery';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useTipWallet } from '../hooks/useTipWallet';
import { useBCHPrice } from '../hooks/useBCHPrice';
import { useBCHAddress } from '../hooks/useBCHAddress';
import { useNip05Verified } from '../hooks/useNip05Verified';
import { Link, useSearchParams } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { timeAgo } from '../lib/utils';
import {
  CAMPAIGN_KIND, CAMPAIGN_D_TAG_PREFIX, CAMPAIGN_TAG, PLEDGE_KIND, PLEDGE_TAG,
  CROWDFUND_FEE_PERCENT, calculateCrowdfundFee, canSendCrowdfundFeeOutput,
  pledgeFeeBreakdown, getCampaignStatus, campaignDTag,
  PLATFORM_FEE_ADDRESS, type CampaignData, type CampaignStatus,
  FEATURED_CAMPAIGN_KIND, FEATURED_CAMPAIGN_D_TAG_PREFIX, FEATURED_CAMPAIGN_TAG,
  FEATURED_TIERS, FEATURED_MAX_SLOTS, FEATURED_MIN_BID_SATS, FEATURED_AUCTION_DURATION,
  isFeaturedActive, parseFeaturedData, type FeaturedCampaignData,
  computeAuctionState, getTopFeaturedIds,
  CAMPAIGN_UPDATE_TAG, CONFIRMATION_TAG, type CampaignUpdate, type CompletionProof,
  type TimelineEntry, buildTimeline, parseCampaignUpdate, calculateReputation,
  RATING_KIND, RATING_TAG, type CampaignRating, parseRating, computeAverageRating, computeCreatorAverageRating,
} from '../lib/crowdfund';
import { BOOST_WALLET, BOOST_KIND, BOOST_D_TAG_PREFIX, BOOST_TAG, BOOST_TIERS, usdToSats, isBoostActive, parseBoostData, type BoostData } from '../lib/boost';
import { useAccountMaturity } from '../hooks/useTrustScore';
import { canCreateCrowdfund, ACCOUNT_AGE } from '../lib/trustScore';

function satsToUSD(sats: number, bchPrice: number | null): string | null {
  if (!bchPrice || bchPrice <= 0) return null;
  const usd = (sats / 1e8) * bchPrice;
  if (usd < 0.01) return '<$0.01';
  return '$' + usd.toFixed(2);
}

function usdToSats(usd: number, bchPrice: number | null): number {
  if (!bchPrice || bchPrice <= 0) return 0;
  return Math.round((usd / bchPrice) * 1e8);
}

export default function CrowdfundPage() {
  const { user, login } = useAppContext();
  const { price: bchPrice } = useBCHPrice();
  const maturity = useAccountMaturity(user?.pubkey);
  const canCreate = maturity.isOperator || maturity.loading || (maturity.firstSeen > 0 && canCreateCrowdfund(maturity.firstSeen));
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignParam = searchParams.get('campaign');
  const [view, setView] = useState<'list' | 'create' | 'detail'>(campaignParam ? 'detail' : 'list');
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(campaignParam);
  const [selectedCampaign, setSelectedCampaign] = useState<{ event: any; data: CampaignData; id: string } | null>(null);

  // Fetch all campaigns — query all relays for complete coverage
  // No `since` filter: campaigns are rare enough that we want ALL of them.
  // Relays naturally prune very old events, so this won't blow up.
  const { events: campaignEvents, refetch } = useNostrQuery({
    filters: [{ kinds: [CAMPAIGN_KIND], '#t': [CAMPAIGN_TAG], limit: 500 }],
    enabled: true,
    allRelays: true,
  });

  // Also fetch the current user's own campaigns separately.
  // This ensures the user always sees their own campaigns even if relays
  // drop them from the main tag-based query (relay data loss, limit hit, etc.)
  const { events: myCampaignEvents } = useNostrQuery({
    filters: user ? [{ kinds: [CAMPAIGN_KIND], authors: [user.pubkey], '#t': [CAMPAIGN_TAG], limit: 50 }] : [],
    enabled: !!user,
    allRelays: true,
  });

  const campaigns = useMemo(() => {
    // Merge main + user's own campaigns, deduplicate by pubkey:d-tag
    // (Kind 30078 is addressable/replaceable — same pubkey+d-tag = same campaign.
    // When a creator updates status, a new event replaces the old one with a new
    // event ID. We must keep only the newest version per pubkey:d-tag and use a
    // STABLE identifier (the "a" tag coordinate) so pledges don't break.)
    const byCoord = new Map<string, any>();
    for (const ev of [...campaignEvents, ...myCampaignEvents]) {
      const dTag = ev.tags?.find((t: string[]) => t[0] === 'd')?.[1] || '';
      const coord = `${CAMPAIGN_KIND}:${ev.pubkey}:${dTag}`;
      const existing = byCoord.get(coord);
      if (!existing || ev.created_at > existing.created_at) {
        byCoord.set(coord, ev);
      }
    }
    return [...byCoord.values()]
      .map(ev => {
        try {
          const data: CampaignData = JSON.parse(ev.content);
          const dTag = ev.tags?.find((t: string[]) => t[0] === 'd')?.[1] || '';
          // Use "a" tag coordinate as stable campaign ID — survives event replacements
          const aTag = `${CAMPAIGN_KIND}:${ev.pubkey}:${dTag}`;
          return { event: ev, data, id: ev.id, aTag, dTag };
        } catch { return null; }
      })
      .filter(Boolean) as { event: any; data: CampaignData; id: string; aTag: string; dTag: string }[];
  }, [campaignEvents, myCampaignEvents]);

  // Fetch featured campaigns — query all relays for reliability
  // Featured slots expire, so a 90-day window is plenty
  const [featuredSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 90);
  const { events: featuredEvents } = useNostrQuery({
    filters: [{ kinds: [FEATURED_CAMPAIGN_KIND], '#t': [FEATURED_CAMPAIGN_TAG], since: featuredSince, limit: 20 }],
    enabled: true,
    allRelays: true,
  });

  // Parse all active featured bids
  const activeFeaturedBids = useMemo(() => {
    const bids: FeaturedCampaignData[] = [];
    featuredEvents.forEach(ev => {
      if (isFeaturedActive(ev)) {
        const data = parseFeaturedData(ev);
        if (data) bids.push(data);
      }
    });
    return bids;
  }, [featuredEvents]);

  // Auction state — determines which campaigns get the featured badge
  const auctionState = useMemo(() => computeAuctionState(activeFeaturedBids), [activeFeaturedBids]);

  // Top 5 by bid get featured; the rest are "outbid" and fall to boosted
  const { featuredIds: featuredCampaignIds, outbidIds: outbidCampaignIds } = useMemo(
    () => getTopFeaturedIds(activeFeaturedBids),
    [activeFeaturedBids],
  );

  // Fetch boosted campaigns (uses same boost system as posts)
  // Query all relays for reliability since boost events are critical
  const [boostSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 2);
  const { events: boostEvents } = useNostrQuery({
    filters: [{ kinds: [BOOST_KIND], '#t': [BOOST_TAG], since: boostSince, limit: 50 }],
    enabled: true,
    allRelays: true,
  });

  // Get actively boosted campaign IDs
  const boostedCampaignMap = useMemo(() => {
    const map = new Map<string, BoostData>();
    boostEvents.forEach(ev => {
      if (isBoostActive(ev)) {
        const data = parseBoostData(ev);
        if (data) {
          // Check if this boosted event ID matches any campaign
          const existing = map.get(data.eventId);
          if (!existing || data.expiresAt > existing.expiresAt) {
            map.set(data.eventId, data);
          }
        }
      }
    });
    return map;
  }, [boostEvents]);

  const boostedCampaignIds = useMemo(() => new Set(boostedCampaignMap.keys()), [boostedCampaignMap]);

  // Fetch ALL pledges in a single query so we can compute status at the list level
  // Query by BOTH #e (old pledges reference event ID) and #a (new pledges reference stable a-tag)
  const allCampaignIds = useMemo(() => campaigns.map(c => c.id), [campaigns]);
  const allCampaignATags = useMemo(() => campaigns.map(c => c.aTag), [campaigns]);
  const { events: allPledges } = useNostrQuery({
    filters: allCampaignIds.length > 0 ? [
      { kinds: [PLEDGE_KIND], '#e': allCampaignIds, limit: 2000 },
      ...(allCampaignATags.length > 0 ? [{ kinds: [PLEDGE_KIND], '#a': allCampaignATags, limit: 2000 }] : []),
    ] : [],
    enabled: allCampaignIds.length > 0,
  });

  // Fetch upvotes (kind 7 reactions) for all campaigns
  const { events: allUpvotes } = useNostrQuery({
    filters: allCampaignIds.length > 0 ? [{ kinds: [7], '#e': allCampaignIds, limit: 500 }] : [],
    enabled: allCampaignIds.length > 0,
  });

  // Fetch star ratings for all campaigns
  const { events: allRatingEvents } = useNostrQuery({
    filters: allCampaignIds.length > 0 ? [{ kinds: [RATING_KIND], '#e': allCampaignIds, '#t': [RATING_TAG], limit: 2000 }] : [],
    enabled: allCampaignIds.length > 0,
  });

  // Build a map of campaignId -> { average, count }
  // Build a map of campaignId -> creator pubkey for self-rating exclusion
  const campaignCreatorMap = useMemo(() => {
    const map = new Map<string, string>();
    campaigns.forEach(c => map.set(c.id, c.event.pubkey));
    return map;
  }, [campaigns]);

  const campaignRatingMap = useMemo(() => {
    const map = new Map<string, { average: number; count: number }>();
    // Group ratings by campaign
    const byCampaign = new Map<string, CampaignRating[]>();
    allRatingEvents.forEach(ev => {
      const r = parseRating(ev);
      if (r) {
        if (!byCampaign.has(r.campaignId)) byCampaign.set(r.campaignId, []);
        byCampaign.get(r.campaignId)!.push(r);
      }
    });
    byCampaign.forEach((ratings, id) => {
      // Exclude creator self-ratings from the average
      const creatorPubkey = campaignCreatorMap.get(id);
      map.set(id, computeAverageRating(ratings, creatorPubkey));
    });
    return map;
  }, [allRatingEvents, campaignCreatorMap]);

  // Build a map of campaignId -> upvote count and set of voter pubkeys
  const campaignUpvoteMap = useMemo(() => {
    const map = new Map<string, { count: number; voters: Set<string> }>();
    allUpvotes.forEach(ev => {
      if (ev.content !== '+' && ev.content !== '') return; // only positive reactions
      const eTags = ev.tags.filter((t: string[]) => t[0] === 'e');
      eTags.forEach((t: string[]) => {
        let entry = map.get(t[1]);
        if (!entry) { entry = { count: 0, voters: new Set() }; map.set(t[1], entry); }
        if (!entry.voters.has(ev.pubkey)) {
          entry.voters.add(ev.pubkey);
          entry.count++;
        }
      });
    });
    return map;
  }, [allUpvotes]);

  // Build a lookup from event ID → aTag for resolving old pledges
  const eventIdToATag = useMemo(() => {
    const map = new Map<string, string>();
    campaigns.forEach(c => map.set(c.id, c.aTag));
    return map;
  }, [campaigns]);

  // Build maps of aTag -> raised sats and aTag -> backer count
  // Pledges can reference campaigns by either #e (old) or #a (new).
  // We normalize everything to aTag for consistent lookup.
  const { campaignRaisedMap, campaignBackerMap } = useMemo(() => {
    const raised = new Map<string, number>();
    const backers = new Map<string, Set<string>>();
    const seenPledgeIds = new Set<string>(); // deduplicate pledges seen via both #e and #a

    allPledges.forEach(ev => {
      if (seenPledgeIds.has(ev.id)) return;
      seenPledgeIds.add(ev.id);

      const amountTag = ev.tags.find((t: string[]) => t[0] === 'amount');
      const amount = amountTag ? parseInt(amountTag[1]) || 0 : 0;

      // Resolve to aTag via #a tag first, then fall back to #e → aTag lookup
      const aTags = ev.tags.filter((t: string[]) => t[0] === 'a').map((t: string[]) => t[1]);
      const eTags = ev.tags.filter((t: string[]) => t[0] === 'e').map((t: string[]) => t[1]);

      const resolvedATags = new Set<string>();
      aTags.forEach(a => resolvedATags.add(a));
      eTags.forEach(eId => {
        const a = eventIdToATag.get(eId);
        if (a) resolvedATags.add(a);
      });

      resolvedATags.forEach(aTag => {
        raised.set(aTag, (raised.get(aTag) || 0) + amount);
        if (!backers.has(aTag)) backers.set(aTag, new Set());
        backers.get(aTag)!.add(ev.pubkey);
      });
    });
    return { campaignRaisedMap: raised, campaignBackerMap: backers };
  }, [allPledges, eventIdToATag]);

  // Compute status for each campaign (keyed by aTag for stable lookup)
  const campaignStatusMap = useMemo(() => {
    const map = new Map<string, CampaignStatus>();
    campaigns.forEach(c => {
      const raised = campaignRaisedMap.get(c.aTag) || 0;
      map.set(c.aTag, getCampaignStatus(c.data, raised));
    });
    return map;
  }, [campaigns, campaignRaisedMap]);

  // Tab filter state
  const [activeTab, setActiveTab] = useState<'active' | 'funded' | 'in_progress' | 'completed' | 'expired' | 'all'>('active');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Count campaigns by status
  const statusCounts = useMemo(() => {
    let active = 0, funded = 0, in_progress = 0, completed = 0, expired = 0;
    campaignStatusMap.forEach(status => {
      if (status === 'active') active++;
      else if (status === 'funded') funded++;
      else if (status === 'in_progress') in_progress++;
      else if (status === 'completed') completed++;
      else expired++;
    });
    return { active, funded, in_progress, completed, expired, total: campaigns.length };
  }, [campaignStatusMap, campaigns]);

  const { featured, boosted, regular } = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    // Apply tab filter + search
    const matchesSearch = (c: { event: any; data: CampaignData; id: string }) => {
      if (!query) return true;
      // Match title
      if (c.data.title.toLowerCase().includes(query)) return true;
      // Match description
      if (c.data.description.toLowerCase().includes(query)) return true;
      // Match creator pubkey prefix
      if (c.event.pubkey.toLowerCase().startsWith(query)) return true;
      // Match creator profile name (from cache — zero network cost)
      const profile = getCachedProfile(c.event.pubkey);
      if (profile?.name?.toLowerCase().includes(query)) return true;
      if (profile?.display_name?.toLowerCase().includes(query)) return true;
      if (profile?.nip05?.toLowerCase().includes(query)) return true;
      return false;
    };

    const tabFilter = (c: { id: string; aTag: string }) => {
      // When searching, show results from all tabs
      if (query) return true;
      const status = campaignStatusMap.get(c.aTag);
      if (activeTab === 'active') return status === 'active';
      if (activeTab === 'funded') return status === 'funded';
      if (activeTab === 'in_progress') return status === 'in_progress';
      if (activeTab === 'completed') return status === 'completed';
      if (activeTab === 'expired') return status === 'expired';
      return true; // 'all' shows everything
    };

    const filtered = campaigns.filter(c => tabFilter(c) && matchesSearch(c));
    const f = filtered.filter(c => featuredCampaignIds.has(c.id));
    // Boosted = explicitly boosted OR outbid from featured auction (they still get boosted visibility)
    const b = filtered.filter(c =>
      !featuredCampaignIds.has(c.id) &&
      (boostedCampaignIds.has(c.id) || outbidCampaignIds.has(c.id))
    );
    const r = filtered.filter(c =>
      !featuredCampaignIds.has(c.id) &&
      !boostedCampaignIds.has(c.id) &&
      !outbidCampaignIds.has(c.id)
    );
    return { featured: f, boosted: b, regular: r };
  }, [campaigns, featuredCampaignIds, outbidCampaignIds, boostedCampaignIds, campaignStatusMap, activeTab, searchQuery]);

  // Sync deep-link param to view state when campaigns load
  useEffect(() => {
    if (campaignParam && campaigns.length > 0) {
      const found = campaigns.find(c => c.id === campaignParam);
      if (found) {
        setSelectedCampaignId(campaignParam);
        setView('detail');
      }
    }
  }, [campaignParam, campaigns]);

  const handleBackToList = () => {
    setView('list');
    setSelectedCampaignId(null);
    setSearchParams({});
  };

  const handleSelectCampaign = (id: string) => {
    setSelectedCampaignId(id);
    setView('detail');
    setSearchParams({ campaign: id });
  };

  if (view === 'create') {
    return <CreateCampaign onBack={handleBackToList} onCreated={() => { handleBackToList(); refetch(); }} bchPrice={bchPrice} />;
  }

  if (view === 'detail' && selectedCampaignId) {
    const campaign = campaigns.find(c => c.id === selectedCampaignId);
    if (campaign) {
      return <CampaignDetail campaign={campaign} onBack={handleBackToList} bchPrice={bchPrice} auctionState={auctionState} />;
    }
  }

  return (
    <div>
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" /> Crowdfunding
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">Fund projects with BCH — direct to creator</p>
        </div>
        {user && canCreate && (
          <button
            onClick={() => setView('create')}
            className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> New Campaign
          </button>
        )}
        {user && !canCreate && !maturity.loading && (
          <div className="text-[10px] text-muted-foreground bg-muted rounded-lg px-3 py-1.5 max-w-[200px] text-center">
            Campaign creation unlocks after {ACCOUNT_AGE.crowdfundMinDays} days
          </div>
        )}
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {!user && (
          <div className="glass-card text-center py-6">
            <Target className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-3">Log in to create or fund campaigns</p>
            <button onClick={login} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">Log In</button>
          </div>
        )}

        {/* Search bar */}
        {campaigns.length > 0 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by title, creator, or keyword..."
              className="w-full pl-9 pr-8 py-2.5 rounded-xl bg-muted/60 border border-border text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-muted-foreground/10"
              >
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            )}
          </div>
        )}

        {campaigns.length === 0 ? (
          <div className="glass-card text-center py-8">
            <Target className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No campaigns yet. Be the first to create one!</p>
          </div>
        ) : (
          <>
            {/* ── Filter Tabs ── */}
            <div className="flex items-center gap-0.5 p-1 bg-muted/60 rounded-xl overflow-x-auto scrollbar-hide">
              {([
                { key: 'active' as const, label: 'Accepting Pledges', icon: <Flame className="w-3 h-3" />, count: statusCounts.active, color: 'text-primary', bg: 'bg-primary/10' },
                { key: 'funded' as const, label: 'Funded', icon: <Target className="w-3 h-3" />, count: statusCounts.funded, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
                { key: 'in_progress' as const, label: 'Building', icon: <Construction className="w-3 h-3" />, count: statusCounts.in_progress, color: 'text-amber-500', bg: 'bg-amber-500/10' },
                { key: 'completed' as const, label: 'Delivered', icon: <CheckCircle2 className="w-3 h-3" />, count: statusCounts.completed, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
                { key: 'all' as const, label: 'All', icon: null, count: statusCounts.total, color: 'text-foreground', bg: 'bg-foreground/10' },
              ].filter(tab => tab.key === 'active' || tab.key === 'all' || tab.count > 0)).map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center justify-center gap-0.5 px-2 py-2 rounded-lg text-[11px] font-semibold transition-all whitespace-nowrap ${
                    activeTab === tab.key
                      ? `bg-background ${tab.color} shadow-sm`
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                  {tab.count > 0 && (
                    <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
                      activeTab === tab.key ? `${tab.bg} ${tab.color}` : 'bg-muted text-muted-foreground'
                    }`}>{tab.count}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Search result count when searching */}
            {searchQuery.trim() && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Search className="w-3.5 h-3.5" />
                <span>
                  {featured.length + boosted.length + regular.length} result{featured.length + boosted.length + regular.length !== 1 ? 's' : ''} for "{searchQuery.trim()}"
                </span>
                <button onClick={() => setSearchQuery('')} className="text-primary hover:underline ml-auto">Clear</button>
              </div>
            )}

            {/* Empty state for active tab / search */}
            {featured.length === 0 && boosted.length === 0 && regular.length === 0 && (
              <div className="glass-card text-center py-6">
                {searchQuery.trim() ? (
                  <>
                    <Search className="w-10 h-10 text-muted-foreground/20 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No campaigns matching "{searchQuery.trim()}"</p>
                    <p className="text-xs text-muted-foreground/70 mt-1">Try a different search term or creator name</p>
                  </>
                ) : activeTab === 'active' ? (
                  <>
                    <Flame className="w-10 h-10 text-muted-foreground/20 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No active campaigns right now</p>
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      {statusCounts.expired > 0 ? (
                        <button onClick={() => setActiveTab('expired')} className="text-amber-500 hover:underline">
                          {statusCounts.expired} ended campaign{statusCounts.expired !== 1 ? 's' : ''} — check the Ended tab
                        </button>
                      ) : statusCounts.funded > 0 ? (
                        <button onClick={() => setActiveTab('funded')} className="text-emerald-500 hover:underline">
                          Check out {statusCounts.funded} fully funded campaign{statusCounts.funded !== 1 ? 's' : ''} →
                        </button>
                      ) : 'Be the first to create one!'}
                    </p>
                  </>
                ) : activeTab === 'funded' ? (
                  <>
                    <Trophy className="w-10 h-10 text-muted-foreground/20 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No fully funded campaigns yet</p>
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      {statusCounts.active > 0 ? (
                        <button onClick={() => setActiveTab('active')} className="text-primary hover:underline">
                          Browse {statusCounts.active} active campaign{statusCounts.active !== 1 ? 's' : ''} and help them reach their goal →
                        </button>
                      ) : 'Be the first to fund one!'}
                    </p>
                  </>
                ) : activeTab === 'expired' ? (
                  <>
                    <Clock className="w-10 h-10 text-muted-foreground/20 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No ended campaigns</p>
                  </>
                ) : (
                  <>
                    <Target className="w-10 h-10 text-muted-foreground/20 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No campaigns found</p>
                  </>
                )}
              </div>
            )}

            {/* Featured campaigns — sorted by upvotes, then recency */}
            {featured.length > 0 && featured.sort((a, b) => {
              const aVotes = campaignUpvoteMap.get(a.id)?.count || 0;
              const bVotes = campaignUpvoteMap.get(b.id)?.count || 0;
              if (bVotes !== aVotes) return bVotes - aVotes;
              return b.event.created_at - a.event.created_at;
            }).map(c => (
              <div key={c.id} className="relative">
                <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 shadow-lg shadow-amber-500/20">
                  <Star className="w-3.5 h-3.5 text-white fill-white" />
                  <span className="text-[10px] font-bold text-white uppercase tracking-wide">Featured</span>
                </div>
                <div className="ring-2 ring-amber-500/30 rounded-2xl">
                  <CampaignCard
                    campaign={c}
                    bchPrice={bchPrice}
                    onClick={() => handleSelectCampaign(c.id)}
                    upvotes={campaignUpvoteMap.get(c.id)}
                    raisedSats={campaignRaisedMap.get(c.aTag) || 0}
                    backerCount={campaignBackerMap.get(c.aTag)?.size || 0}
                    avgRating={campaignRatingMap.get(c.id)}
                  />
                </div>
              </div>
            ))}

            {/* Boosted campaigns — sorted by upvotes, then recency */}
            {boosted.length > 0 && boosted.sort((a, b) => {
              const aVotes = campaignUpvoteMap.get(a.id)?.count || 0;
              const bVotes = campaignUpvoteMap.get(b.id)?.count || 0;
              if (bVotes !== aVotes) return bVotes - aVotes;
              return b.event.created_at - a.event.created_at;
            }).map(c => (
              <div key={c.id} className="relative">
                <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-primary to-emerald-500 shadow-lg shadow-primary/20">
                  <Rocket className="w-3.5 h-3.5 text-white" />
                  <span className="text-[10px] font-bold text-white uppercase tracking-wide">Boosted</span>
                </div>
                <div className="ring-2 ring-primary/30 rounded-2xl">
                  <CampaignCard
                    campaign={c}
                    bchPrice={bchPrice}
                    onClick={() => handleSelectCampaign(c.id)}
                    upvotes={campaignUpvoteMap.get(c.id)}
                    raisedSats={campaignRaisedMap.get(c.aTag) || 0}
                    backerCount={campaignBackerMap.get(c.aTag)?.size || 0}
                    avgRating={campaignRatingMap.get(c.id)}
                  />
                </div>
              </div>
            ))}

            {/* Regular campaigns — sorted by upvotes, then recency */}
            {regular.sort((a, b) => {
              const aVotes = campaignUpvoteMap.get(a.id)?.count || 0;
              const bVotes = campaignUpvoteMap.get(b.id)?.count || 0;
              if (bVotes !== aVotes) return bVotes - aVotes;
              return b.event.created_at - a.event.created_at;
            }).map(c => (
              <CampaignCard
                key={c.id}
                campaign={c}
                bchPrice={bchPrice}
                onClick={() => handleSelectCampaign(c.id)}
                upvotes={campaignUpvoteMap.get(c.id)}
                raisedSats={campaignRaisedMap.get(c.aTag) || 0}
                backerCount={campaignBackerMap.get(c.aTag)?.size || 0}
                avgRating={campaignRatingMap.get(c.id)}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ── Campaign Card ────────────────────────────────────────────────
function CampaignCard({ campaign, bchPrice, onClick, upvotes, raisedSats: parentRaised, backerCount: parentBackers, avgRating: parentRating }: {
  campaign: { event: any; data: CampaignData; id: string };
  bchPrice: number | null;
  onClick: () => void;
  upvotes?: { count: number; voters: Set<string> };
  /** Pre-computed raised sats from parent (avoids per-card pledge query) */
  raisedSats?: number;
  /** Pre-computed backer count from parent */
  backerCount?: number;
  /** Pre-computed average rating */
  avgRating?: { average: number; count: number };
}) {
  const { profile } = useNostrProfile(campaign.event.pubkey);
  const { data } = campaign;

  // Use parent-provided data when available; fall back to own query (e.g. detail pages)
  const needOwnQuery = parentRaised === undefined;
  const { events: pledges } = useNostrQuery({
    filters: [{ kinds: [PLEDGE_KIND], '#e': [campaign.id], limit: 200 }],
    enabled: needOwnQuery,
  });

  const raisedSats = parentRaised ?? pledges.reduce((sum, ev) => {
    const amountTag = ev.tags.find((t: string[]) => t[0] === 'amount');
    return sum + (amountTag ? parseInt(amountTag[1]) || 0 : 0);
  }, 0);
  const numBackers = parentBackers ?? pledges.length;

  const status = getCampaignStatus(data, raisedSats);
  const pct = Math.min(100, Math.round((raisedSats / data.goalSats) * 100));
  const daysLeft = Math.max(0, Math.ceil((data.deadline - Date.now() / 1000) / 86400));
  const upvoteCount = upvotes?.count || 0;
  const rating = parentRating;

  return (
    <button onClick={onClick} className="glass-card w-full text-left hover:border-primary/30 transition-colors">
      {data.image && (
        <div className="-m-4 mb-3 rounded-t-xl overflow-hidden">
          <img src={data.image} alt="" className="w-full h-36 object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        </div>
      )}
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge status={status} />
            <h3 className="text-sm font-bold truncate">{data.title}</h3>
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{data.description}</p>

          {/* Progress bar */}
          <div className="h-2 rounded-full bg-muted overflow-hidden mb-2">
            <div
              className={`h-full rounded-full transition-all ${status === 'funded' ? 'bg-emerald-500' : 'bg-primary'}`}
              style={{ width: `${pct}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-xs">
            <span className="font-bold text-primary">{satsToUSD(raisedSats, bchPrice) || `${raisedSats.toLocaleString()} sats`}</span>
            <span className="text-muted-foreground">of {satsToUSD(data.goalSats, bchPrice) || `${data.goalSats.toLocaleString()} sats`}</span>
          </div>
          <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-1">
            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {numBackers} backer{numBackers !== 1 ? 's' : ''}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {status === 'expired' ? 'Ended' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
        <div className="flex items-center gap-2">
          {profile?.picture ? (
            <img src={profile.picture} alt="" className="w-5 h-5 rounded-full object-cover" />
          ) : (
            <div className="w-5 h-5 rounded-full bg-primary/10" />
          )}
          <span className="text-[10px] text-muted-foreground">by {profile?.name || campaign.event.pubkey.slice(0, 8)}</span>
        </div>
        <div className="flex items-center gap-2">
          {rating && rating.count > 0 && (
            <div className="flex items-center gap-1">
              <StarRatingDisplay rating={rating.average} size="xs" />
              <span className="text-[10px] text-muted-foreground">({rating.count})</span>
            </div>
          )}
          {upvoteCount > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-emerald-500/10 to-primary/10 border border-emerald-500/20">
              <span className="text-sm">🔥</span>
              <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">{upvoteCount}</span>
              <span className="text-[10px] text-muted-foreground font-medium">vote{upvoteCount !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

function StatusBadge({ status, size = 'sm' }: { status: CampaignStatus; size?: 'sm' | 'md' }) {
  const base = size === 'md' ? 'text-[10px] px-2 py-0.5' : 'text-[9px] px-1.5 py-0.5';
  if (status === 'completed') return <span className={`${base} bg-emerald-500/10 text-emerald-500 rounded-full font-semibold flex items-center gap-1`}><CheckCircle2 className="w-2.5 h-2.5" /> Delivered</span>;
  if (status === 'in_progress') return <span className={`${base} bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-full font-semibold flex items-center gap-1`}><Construction className="w-2.5 h-2.5" /> Building</span>;
  if (status === 'funded') return <span className={`${base} bg-emerald-500/10 text-emerald-500 rounded-full font-semibold flex items-center gap-1`}><Target className="w-2.5 h-2.5" /> Funded</span>;
  if (status === 'expired') return <span className={`${base} bg-red-500/10 text-red-500 rounded-full font-semibold flex items-center gap-1`}><Clock className="w-2.5 h-2.5" /> Ended</span>;
  return <span className={`${base} bg-primary/10 text-primary rounded-full font-semibold flex items-center gap-1`}><span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" /> Accepting Pledges</span>;
}

// ── Campaign Upvote ─────────────────────────────────────────────
// Only verified @bchnostr.com users can upvote campaigns.
function CampaignUpvote({ campaignId, campaignPubkey }: { campaignId: string; campaignPubkey: string }) {
  const { user, login } = useAppContext();
  const { publish } = useNostrPublish();
  const { profile: myProfile } = useNostrProfile(user?.pubkey);
  const isVerified = useNip05Verified(myProfile?.nip05, user?.pubkey);
  const [voting, setVoting] = useState(false);
  const [justVoted, setJustVoted] = useState(false);

  // Fetch existing upvotes for this campaign
  const { events: upvoteEvents } = useNostrQuery({
    filters: [{ kinds: [7], '#e': [campaignId], limit: 200 }],
    enabled: true,
  });

  // Count unique upvotes (only + reactions)
  const { count, voters, hasVoted } = useMemo(() => {
    const voters = new Set<string>();
    upvoteEvents.forEach(ev => {
      if (ev.content === '+' || ev.content === '') {
        voters.add(ev.pubkey);
      }
    });
    return {
      count: voters.size,
      voters,
      hasVoted: user ? voters.has(user.pubkey) : false,
    };
  }, [upvoteEvents, user]);

  const alreadyVoted = hasVoted || justVoted;

  const handleUpvote = useCallback(async () => {
    if (!user || !isVerified || alreadyVoted || voting) return;
    setVoting(true);
    try {
      await publish({
        kind: 7,
        content: '+',
        tags: [
          ['e', campaignId],
          ['p', campaignPubkey],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setJustVoted(true);
    } catch {}
    setVoting(false);
  }, [user, isVerified, alreadyVoted, voting, publish, campaignId, campaignPubkey]);

  const displayCount = count + (justVoted && !hasVoted ? 1 : 0);

  return (
    <div className={`rounded-2xl border overflow-hidden ${
      alreadyVoted
        ? 'bg-gradient-to-br from-emerald-500/5 via-primary/5 to-emerald-500/5 border-emerald-500/20'
        : 'bg-gradient-to-br from-primary/5 via-background to-emerald-500/5 border-border'
    }`}>
      {/* Vote count banner */}
      <div className="flex items-center justify-center gap-3 py-4 px-4">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <span className="text-2xl">🔥</span>
            <span className="text-3xl font-black tabular-nums text-foreground">{displayCount}</span>
          </div>
          <p className="text-xs font-semibold text-muted-foreground">
            Community Vote{displayCount !== 1 ? 's' : ''}
          </p>
          <p className="text-[10px] text-muted-foreground/70 mt-0.5 flex items-center justify-center gap-1">
            <BadgeCheck className="w-3 h-3 text-emerald-500" />
            From verified BCHNostr members
          </p>
        </div>
      </div>

      {/* Action area */}
      <div className="px-4 pb-4">
        {!user ? (
          <button
            onClick={login}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-primary to-emerald-500 text-white text-sm font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-lg shadow-primary/20"
          >
            <ThumbsUp className="w-4 h-4" />
            Log in to Vote
          </button>
        ) : alreadyVoted ? (
          <div className="w-full py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-sm font-bold flex items-center justify-center gap-2">
            <Check className="w-4.5 h-4.5" />
            You Voted for This Campaign!
          </div>
        ) : isVerified ? (
          <button
            onClick={handleUpvote}
            disabled={voting}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-primary to-emerald-500 text-white text-sm font-bold flex items-center justify-center gap-2 hover:opacity-90 hover:scale-[1.01] active:scale-[0.99] transition-all shadow-lg shadow-primary/20 disabled:opacity-50 disabled:hover:scale-100"
          >
            {voting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <ThumbsUp className="w-4.5 h-4.5" />
                <span className="text-base">Vote for This Campaign</span>
              </>
            )}
            {voting && 'Submitting Vote...'}
          </button>
        ) : (
          <div className="space-y-2">
            <div className="w-full py-3 rounded-xl bg-muted border border-border text-muted-foreground text-sm font-medium flex items-center justify-center gap-2">
              <BadgeCheck className="w-4 h-4" />
              Verified Members Only
            </div>
            <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
              Get a free <strong>@bchnostr.com</strong> NIP-05 in your profile settings to unlock voting
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Campaign Detail ──────────────────────────────────────────────
function CampaignDetail({ campaign, onBack, bchPrice, auctionState }: { campaign: { event: any; data: CampaignData; id: string; aTag?: string; dTag?: string }; onBack: () => void; bchPrice: number | null; auctionState: ReturnType<typeof computeAuctionState> }) {
  const { user, login } = useAppContext();
  const { publish } = useNostrPublish();
  const tipWallet = useTipWallet();
  const { profile } = useNostrProfile(campaign.event.pubkey);
  const { data } = campaign;
  const [pledgeAmount, setPledgeAmount] = useState('');
  const [pledging, setPledging] = useState(false);
  const [pledgeError, setPledgeError] = useState<string | null>(null);
  const [pledgeSuccess, setPledgeSuccess] = useState(false);
  const [pledgeViralNote, setPledgeViralNote] = useState<string | null>(null);
  const [walletInitializing, setWalletInitializing] = useState(false);

  // Campaign update state
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [updateContent, setUpdateContent] = useState('');
  const [updateImages, setUpdateImages] = useState<string[]>([]);
  const [updateImageUploading, setUpdateImageUploading] = useState(false);
  const updateImageRef = useRef<HTMLInputElement>(null);
  const [publishingUpdate, setPublishingUpdate] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Status management state
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<CampaignStatus | null>(null);

  // Completion proof state
  const [showCompletionForm, setShowCompletionForm] = useState(false);
  const [completionDesc, setCompletionDesc] = useState('');
  const [completionImages, setCompletionImages] = useState<string[]>([]);
  const [completionLinks, setCompletionLinks] = useState('');
  const [completionImageUploading, setCompletionImageUploading] = useState(false);
  const completionImageRef = useRef<HTMLInputElement>(null);
  const [completingCampaign, setCompletingCampaign] = useState(false);

  // Timeline state
  const [showTimeline, setShowTimeline] = useState(false);

  // Community confirmation state
  const [confirming, setConfirmingDelivery] = useState(false);
  const [confirmSuccess, setConfirmSuccess] = useState(false);

  // Edit/delete state
  const [showOwnerMenu, setShowOwnerMenu] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState(data.title);
  const [editDescription, setEditDescription] = useState(data.description);
  const [editImage, setEditImage] = useState(data.image || '');
  const [editImageUploading, setEditImageUploading] = useState(false);
  const editImageFileRef = useRef<HTMLInputElement>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Stretch goal state
  const [showStretchGoal, setShowStretchGoal] = useState(false);
  const [stretchGoalUSD, setStretchGoalUSD] = useState('');
  const [stretchExtendDays, setStretchExtendDays] = useState(30);
  const [stretchSaving, setStretchSaving] = useState(false);
  const [stretchError, setStretchError] = useState<string | null>(null);

  const isOwner = user?.pubkey === campaign.event.pubkey;

  // On-chain balance — fetched directly from the BCH address so QR donations count
  const [onChainSats, setOnChainSats] = useState<number | null>(null);

  useEffect(() => {
    if (!data.bchAddress) return;
    const addr = data.bchAddress.replace(/^bitcoincash:/i, '');
    const url = `https://proxy.shakespeare.diy/?url=${encodeURIComponent(`https://bchexplorer.cash/api/v1/address/${addr}`)}`;

    let cancelled = false;
    const fetchBalance = () => {
      fetch(url, { signal: AbortSignal.timeout(8000) })
        .then(r => r.json())
        .then(json => {
          if (cancelled) return;
          const confirmed = (json?.chain_stats?.funded_txo_sum ?? 0) - (json?.chain_stats?.spent_txo_sum ?? 0);
          const unconfirmed = (json?.mempool_stats?.funded_txo_sum ?? 0) - (json?.mempool_stats?.spent_txo_sum ?? 0);
          setOnChainSats(Math.max(0, confirmed + unconfirmed));
        })
        .catch(() => {});
    };

    // Fetch once on mount, then every 5 minutes passively
    fetchBalance();
    const timer = setInterval(fetchBalance, 5 * 60_000);

    // Also refresh when the user comes back to the tab
    const onVisible = () => { if (!cancelled && document.visibilityState === 'visible') fetchBalance(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [data.bchAddress]);

  // Fetch pledges (Nostr events published by in-app pledge flow)
  // Query by both #e (old pledges reference event ID) and #a (new pledges reference stable a-tag)
  const campaignATag = campaign.aTag || `${CAMPAIGN_KIND}:${campaign.event.pubkey}:${campaign.dTag || campaign.event.tags?.find((t: string[]) => t[0] === 'd')?.[1] || ''}`;
  const { events: rawPledges, refetch: refetchPledges } = useNostrQuery({
    filters: [
      { kinds: [PLEDGE_KIND], '#e': [campaign.id], limit: 200 },
      { kinds: [PLEDGE_KIND], '#a': [campaignATag], limit: 200 },
    ],
    enabled: true,
  });
  // Deduplicate pledges that might be found by both queries
  const pledges = useMemo(() => {
    const seen = new Set<string>();
    return rawPledges.filter(ev => {
      if (seen.has(ev.id)) return false;
      seen.add(ev.id);
      return true;
    });
  }, [rawPledges]);

  // Fetch creator updates for this campaign
  const { events: updateEvents, refetch: refetchUpdates } = useNostrQuery({
    filters: [{ kinds: [1], '#t': [CAMPAIGN_UPDATE_TAG], '#e': [campaign.id], limit: 50 }],
    enabled: true,
  });

  const campaignUpdates = useMemo(() => {
    return updateEvents
      .filter(ev => ev.pubkey === campaign.event.pubkey)
      .map(ev => parseCampaignUpdate(ev))
      .filter(Boolean) as CampaignUpdate[];
  }, [updateEvents, campaign.event.pubkey]);

  // Fetch community confirmations
  const { events: confirmationEvents, refetch: refetchConfirmations } = useNostrQuery({
    filters: [{ kinds: [7], '#e': [campaign.id], '#t': [CONFIRMATION_TAG], limit: 200 }],
    enabled: true,
  });

  const { confirmationCount, hasConfirmed } = useMemo(() => {
    const voters = new Set<string>();
    confirmationEvents.forEach(ev => {
      if (ev.tags?.some((t: string[]) => t[0] === 't' && t[1] === CONFIRMATION_TAG)) {
        voters.add(ev.pubkey);
      }
    });
    return {
      confirmationCount: voters.size,
      hasConfirmed: user ? voters.has(user.pubkey) : false,
    };
  }, [confirmationEvents, user]);

  const nostrRaisedSats = pledges.reduce((sum, ev) => {
    const amountTag = ev.tags.find((t: string[]) => t[0] === 'amount');
    return sum + (amountTag ? parseInt(amountTag[1]) || 0 : 0);
  }, 0);

  // Use whichever is higher: on-chain balance (ground truth) or Nostr pledge sum.
  // On-chain is authoritative but may lag slightly; Nostr pledges update instantly
  // for in-app pledges. Taking the max prevents the bar from jumping backwards.
  //
  // IMPORTANT: Only use on-chain balance when there are actual Nostr pledges.
  // Without this guard, pre-existing funds on the BCH address (unrelated to the
  // campaign) would show up as "raised" — creating a false progress bar for
  // newly created campaigns that haven't received any real pledges yet.
  const hasNostrPledges = pledges.length > 0;
  const raisedSats = hasNostrPledges && onChainSats !== null
    ? Math.max(onChainSats, nostrRaisedSats)
    : nostrRaisedSats;

  // Build timeline (after raisedSats is computed)
  const timeline = useMemo(() => {
    return buildTimeline(data, campaign.event, pledges, campaignUpdates, confirmationEvents, raisedSats);
  }, [data, campaign.event, pledges, campaignUpdates, confirmationEvents, raisedSats]);

  // Check if user is a backer (for community confirmation eligibility)
  const isBackerUser = useMemo(() => {
    if (!user) return false;
    return pledges.some(ev => ev.pubkey === user.pubkey);
  }, [pledges, user]);

  const status = getCampaignStatus(data, raisedSats);
  const pct = Math.min(100, Math.round((raisedSats / data.goalSats) * 100));
  const daysLeft = Math.max(0, Math.ceil((data.deadline - Date.now() / 1000) / 86400));

  const pledgeSats = bchPrice && pledgeAmount ? usdToSats(parseFloat(pledgeAmount) || 0, bchPrice) : 0;
  const breakdown = pledgeFeeBreakdown(pledgeSats);

  const handleInitWallet = async () => {
    setWalletInitializing(true);
    try { await tipWallet.init(); } catch {}
    setWalletInitializing(false);
  };

  const handlePledge = async () => {
    if (!user || pledgeSats <= 0 || !data.bchAddress) return;
    setPledging(true);
    setPledgeError(null);

    try {
      if (!tipWallet.ready) throw new Error('Wallet not ready');

      const fee = calculateCrowdfundFee(pledgeSats);
      const net = pledgeSats - fee;
      if (net <= 0) throw new Error('Pledge amount too small');

      // Build outputs for a single transaction
      const outputs: { cashaddr: string; value: bigint }[] = [];
      outputs.push({ cashaddr: data.bchAddress, value: BigInt(net) });

      // Add platform fee as second output if above dust limit
      if (canSendCrowdfundFeeOutput(fee)) {
        outputs.push({ cashaddr: PLATFORM_FEE_ADDRESS, value: BigInt(fee) });
      }

      // Send in a single transaction with multiple outputs
      const txId = await tipWallet.sendMultiOutput(outputs);

      // Publish pledge receipt to Nostr (private, for tracking)
      // Include both 'e' (event ID) and 'a' (stable coordinate) tags so
      // pledges survive campaign event replacements (status changes, edits).
      await publish({
        kind: PLEDGE_KIND,
        content: `Pledged ${satsToUSD(pledgeSats, bchPrice) || `${pledgeSats.toLocaleString()} sats`} to "${data.title}" #bch-crowdfund #bitcoincash`,
        tags: [
          ['e', campaign.id],
          ['a', campaignATag],
          ['p', campaign.event.pubkey],
          ['t', PLEDGE_TAG],
          ['t', 'bch'],
          ['t', 'bitcoincash'],
          ['amount', String(pledgeSats)],
          ['txid', txId],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      // Publish a viral public note announcing the contribution
      const creatorNpub = (() => { try { return `nostr:${nip19.npubEncode(campaign.event.pubkey)}`; } catch { return ''; } })();
      const campaignUrl = `https://bchnostr.com/crowdfund?campaign=${campaign.id}`;
      const viralMessages = [
        `\uD83D\uDFE2 I just backed "${data.title}" with Bitcoin Cash on BCHNostr! Every satoshi counts — join me and help make this happen! \uD83D\uDC47\n\n${campaignUrl}\n\n#BCH #BitcoinCash #bchnostr`,
        `💚 Just contributed to "${data.title}" — a campaign I believe in. Check it out and back it with BCH if you feel the same!\n\n${campaignUrl}\n\n#BCH #BitcoinCash #bchnostr`,
        `\u26A1 Backed! Just sent BCH to support "${data.title}" on BCHNostr. P2P crowdfunding is the future 🚀\n\n${campaignUrl}\n\n#BCH #BitcoinCash #bchnostr`,
        `\uD83D\uDE4C Proud to back "${data.title}" with Bitcoin Cash! No banks, no middlemen — just people supporting people.\n\n${campaignUrl}\n\n#BCH #BitcoinCash #bchnostr`,
      ];
      const viralNote = viralMessages[Math.floor(Math.random() * viralMessages.length)];

      publish({
        kind: 1,
        content: viralNote,
        tags: [
          ...(creatorNpub ? [['p', campaign.event.pubkey]] : []),
          ['t', 'bch'],
          ['t', 'bitcoincash'],
          ['t', 'bchnostr'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      }).catch(() => {});

      setPledgeSuccess(true);
      setPledgeViralNote(viralNote);
      tipWallet.refreshBalance();
      setTimeout(() => refetchPledges(), 2000);
    } catch (e) {
      setPledgeError(e instanceof Error ? e.message : 'Pledge failed');
    } finally {
      setPledging(false);
    }
  };

  // ── Edit campaign handler ──
  const handleSaveEdit = async () => {
    if (!user || !editTitle.trim()) return;
    setEditSaving(true);
    setEditError(null);
    try {
      // Extract the d-tag from the original event
      const dTag = campaign.event.tags?.find((t: string[]) => t[0] === 'd')?.[1];
      if (!dTag) throw new Error('Cannot edit: missing campaign identifier');

      const updatedData: CampaignData = {
        ...data,
        title: editTitle.trim(),
        description: editDescription.trim(),
        image: editImage.trim() || undefined,
      };

      await publish({
        kind: CAMPAIGN_KIND,
        content: JSON.stringify(updatedData),
        tags: [
          ['d', dTag],
          ['t', CAMPAIGN_TAG],
          ['t', 'bch'],
          ['t', 'bitcoincash'],
          ['goal', String(data.goalSats)],
          ['deadline', String(data.deadline)],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      // Update local state
      campaign.data.title = updatedData.title;
      campaign.data.description = updatedData.description;
      campaign.data.image = updatedData.image;
      setEditMode(false);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Failed to save changes');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Delete campaign handler ──
  const handleDelete = async () => {
    if (!user) return;
    setDeleting(true);
    try {
      // Publish a kind 5 deletion event per NIP-09
      await publish({
        kind: 5,
        content: 'Campaign deleted by owner',
        tags: [
          ['e', campaign.id],
          // Also reference by a-tag for addressable event deletion
          ...(campaign.event.tags?.find((t: string[]) => t[0] === 'd')
            ? [['a', `${CAMPAIGN_KIND}:${campaign.event.pubkey}:${campaign.event.tags.find((t: string[]) => t[0] === 'd')[1]}`]]
            : []),
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      onBack(); // Go back to list
    } catch (e) {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // ── Status change handler ──
  const handleStatusChange = async (newStatus: CampaignStatus) => {
    if (!user) return;
    setStatusChanging(true);
    try {
      const dTag = campaign.event.tags?.find((t: string[]) => t[0] === 'd')?.[1];
      if (!dTag) throw new Error('Missing campaign identifier');

      const updatedData: CampaignData = {
        ...data,
        status: newStatus,
        ...(newStatus === 'completed' ? { completedAt: Math.floor(Date.now() / 1000) } : {}),
      };

      await publish({
        kind: CAMPAIGN_KIND,
        content: JSON.stringify(updatedData),
        tags: [
          ['d', dTag],
          ['t', CAMPAIGN_TAG],
          ['t', 'bch'],
          ['t', 'bitcoincash'],
          ['goal', String(data.goalSats)],
          ['deadline', String(data.deadline)],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      campaign.data.status = newStatus;
      if (newStatus === 'completed') campaign.data.completedAt = Math.floor(Date.now() / 1000);
      setShowStatusMenu(false);
    } catch (e) {
      console.error('Status change failed:', e);
    } finally {
      setStatusChanging(false);
    }
  };

  // ── Post campaign update ──
  const handlePostUpdate = async () => {
    if (!user || !updateContent.trim()) return;
    setPublishingUpdate(true);
    try {
      const imageLines = updateImages.map(url => `\n${url}`).join('');
      await publish({
        kind: 1,
        content: `${updateContent.trim()}${imageLines}`,
        tags: [
          ['e', campaign.id],
          ['p', campaign.event.pubkey],
          ['t', CAMPAIGN_UPDATE_TAG],
          ['t', 'bch'],
          ['t', 'bitcoincash'],
          ...(updateImages.map(url => ['imeta', `url ${url}`, 'm image/*'])),
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setUpdateContent('');
      setUpdateImages([]);
      setShowUpdateForm(false);
      setUpdateSuccess(true);
      setTimeout(() => { setUpdateSuccess(false); refetchUpdates(); }, 2000);
    } catch (e) {
      console.error('Update post failed:', e);
    } finally {
      setPublishingUpdate(false);
    }
  };

  // ── Complete campaign with proof ──
  const handleCompleteCampaign = async () => {
    if (!user) return;
    setCompletingCampaign(true);
    try {
      const dTag = campaign.event.tags?.find((t: string[]) => t[0] === 'd')?.[1];
      if (!dTag) throw new Error('Missing campaign identifier');

      const proofLinks = completionLinks.trim().split('\n').map(l => l.trim()).filter(Boolean);
      const proof: CompletionProof = {
        description: completionDesc.trim(),
        images: completionImages.length > 0 ? completionImages : undefined,
        links: proofLinks.length > 0 ? proofLinks : undefined,
        completedAt: Math.floor(Date.now() / 1000),
      };

      const updatedData: CampaignData = {
        ...data,
        status: 'completed',
        completedAt: Math.floor(Date.now() / 1000),
        completionProof: proof,
      };

      await publish({
        kind: CAMPAIGN_KIND,
        content: JSON.stringify(updatedData),
        tags: [
          ['d', dTag],
          ['t', CAMPAIGN_TAG],
          ['t', 'bch'],
          ['t', 'bitcoincash'],
          ['goal', String(data.goalSats)],
          ['deadline', String(data.deadline)],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      // Also post a public update note
      const imageLines = completionImages.map(url => `\n${url}`).join('');
      await publish({
        kind: 1,
        content: `✅ Campaign "${data.title}" is complete!\n\n${completionDesc.trim()}${imageLines}\n\nhttps://bchnostr.com/crowdfund?campaign=${campaign.id}\n\n#BCH #BitcoinCash #bchnostr`,
        tags: [
          ['e', campaign.id],
          ['t', CAMPAIGN_UPDATE_TAG],
          ['t', 'bch'],
          ['t', 'bitcoincash'],
          ...(completionImages.map(url => ['imeta', `url ${url}`, 'm image/*'])),
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      campaign.data.status = 'completed';
      campaign.data.completedAt = updatedData.completedAt;
      campaign.data.completionProof = proof;
      setShowCompletionForm(false);
    } catch (e) {
      console.error('Completion failed:', e);
    } finally {
      setCompletingCampaign(false);
    }
  };

  // ── Community confirmation ──
  const handleConfirmDelivery = async () => {
    if (!user || hasConfirmed) return;
    setConfirmingDelivery(true);
    try {
      await publish({
        kind: 7,
        content: '+',
        tags: [
          ['e', campaign.id],
          ['p', campaign.event.pubkey],
          ['t', CONFIRMATION_TAG],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setConfirmSuccess(true);
      setTimeout(() => refetchConfirmations(), 2000);
    } catch (e) {
      console.error('Confirmation failed:', e);
    } finally {
      setConfirmingDelivery(false);
    }
  };

  // ── Stretch goal handler ──
  // When a campaign is funded, the owner can increase the goal to keep momentum.
  // This republishes the campaign event with a higher goalSats and optional new deadline.
  const stretchGoalSats = bchPrice && stretchGoalUSD ? usdToSats(parseFloat(stretchGoalUSD) || 0, bchPrice) : 0;
  const stretchGoalValid = stretchGoalSats > data.goalSats;

  const handleStretchGoal = async () => {
    if (!user || !stretchGoalValid) return;
    setStretchSaving(true);
    setStretchError(null);
    try {
      const dTag = campaign.event.tags?.find((t: string[]) => t[0] === 'd')?.[1];
      if (!dTag) throw new Error('Cannot update: missing campaign identifier');

      const now = Math.floor(Date.now() / 1000);
      // Extend deadline from now (not from the old deadline, since it may already have passed)
      const newDeadline = now + stretchExtendDays * 86400;

      const updatedData: CampaignData = {
        ...data,
        goalSats: stretchGoalSats,
        deadline: newDeadline,
      };

      await publish({
        kind: CAMPAIGN_KIND,
        content: JSON.stringify(updatedData),
        tags: [
          ['d', dTag],
          ['t', CAMPAIGN_TAG],
          ['t', 'bch'],
          ['t', 'bitcoincash'],
          ['goal', String(stretchGoalSats)],
          ['deadline', String(newDeadline)],
        ],
        created_at: now,
      });

      // Update local state so UI reflects changes immediately
      campaign.data.goalSats = stretchGoalSats;
      campaign.data.deadline = newDeadline;
      setShowStretchGoal(false);
      setStretchGoalUSD('');
    } catch (e) {
      setStretchError(e instanceof Error ? e.message : 'Failed to update goal');
    } finally {
      setStretchSaving(false);
    }
  };

  return (
    <div>
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3 flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to Campaigns
        </button>
        {/* Owner: edit/delete menu */}
        {isOwner && !editMode && (
          <div className="relative">
            <button
              onClick={() => setShowOwnerMenu(!showOwnerMenu)}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
            </button>
            {showOwnerMenu && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setShowOwnerMenu(false)} />
                <div className="absolute right-0 top-full mt-1 z-30 bg-popover border border-border rounded-xl shadow-lg overflow-hidden min-w-[180px]">
                  {/* Post update */}
                  <button
                    onClick={() => { setShowUpdateForm(true); setShowOwnerMenu(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-xs hover:bg-primary/10 transition-colors text-primary font-medium"
                  >
                    <MessageSquarePlus className="w-3.5 h-3.5" /> Post Update
                  </button>
                  {/* Status changes — with confirmation */}
                  {(status === 'funded' || status === 'active') && (
                    <button
                      onClick={() => { setPendingStatusChange('in_progress'); setShowOwnerMenu(false); }}
                      disabled={statusChanging}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-xs hover:bg-amber-500/10 transition-colors text-amber-600 dark:text-amber-400 font-medium"
                    >
                      <Construction className="w-3.5 h-3.5" /> Mark as Building
                    </button>
                  )}
                  {status === 'in_progress' && (
                    <button
                      onClick={() => { setPendingStatusChange('funded'); setShowOwnerMenu(false); }}
                      disabled={statusChanging}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-xs hover:bg-blue-500/10 transition-colors text-blue-600 dark:text-blue-400 font-medium"
                    >
                      <Target className="w-3.5 h-3.5" /> Revert to Funded
                    </button>
                  )}
                  {(status === 'funded' || status === 'in_progress') && (
                    <button
                      onClick={() => { setShowCompletionForm(true); setShowOwnerMenu(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-xs hover:bg-emerald-500/10 transition-colors text-emerald-600 dark:text-emerald-400 font-medium"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Mark as Completed
                    </button>
                  )}
                  {status === 'funded' && (
                    <button
                      onClick={() => { setShowStretchGoal(true); setShowOwnerMenu(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-xs hover:bg-emerald-500/10 transition-colors text-emerald-600 dark:text-emerald-400 font-medium"
                    >
                      <TrendingUp className="w-3.5 h-3.5" /> Set Stretch Goal
                    </button>
                  )}
                  <div className="border-t border-border" />
                  <button
                    onClick={() => { setEditMode(true); setShowOwnerMenu(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-xs hover:bg-muted transition-colors text-foreground"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit Campaign
                  </button>
                  <button
                    onClick={() => { setShowDeleteConfirm(true); setShowOwnerMenu(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-xs hover:bg-red-500/10 transition-colors text-red-500"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete Campaign
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Delete confirmation dialog ── */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-card border border-border rounded-2xl w-full max-w-sm shadow-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Delete Campaign?</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  This will request deletion of <strong>"{data.title}"</strong> from relays. This action cannot be undone. Existing pledges are not affected.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Status change confirmation dialog ── */}
      {pendingStatusChange && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPendingStatusChange(null)}>
          <div className="bg-card border border-border rounded-2xl w-full max-w-sm shadow-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                pendingStatusChange === 'in_progress' ? 'bg-amber-500/10' : 'bg-blue-500/10'
              }`}>
                {pendingStatusChange === 'in_progress'
                  ? <Construction className="w-5 h-5 text-amber-500" />
                  : <Target className="w-5 h-5 text-blue-500" />
                }
              </div>
              <div>
                <h3 className="font-bold text-sm">
                  {pendingStatusChange === 'in_progress' ? 'Mark as Building?' : 'Revert to Funded?'}
                </h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {pendingStatusChange === 'in_progress'
                    ? 'This tells backers you\'ve started working on the project. You can revert this later if needed. Backer data will be preserved.'
                    : 'This will move the campaign back to "Funded" status. Use this if you accidentally changed the status.'
                  }
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPendingStatusChange(null)}
                disabled={statusChanging}
                className="flex-1 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleStatusChange(pendingStatusChange);
                  setPendingStatusChange(null);
                }}
                disabled={statusChanging}
                className={`flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-colors flex items-center justify-center gap-2 ${
                  pendingStatusChange === 'in_progress' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-500 hover:bg-blue-600'
                }`}
              >
                {statusChanging ? <Loader2 className="w-4 h-4 animate-spin" /> : pendingStatusChange === 'in_progress' ? <Construction className="w-4 h-4" /> : <Target className="w-4 h-4" />}
                {statusChanging ? 'Updating...' : pendingStatusChange === 'in_progress' ? 'Yes, Mark as Building' : 'Yes, Revert'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* ── Edit mode ── */}
        {editMode && isOwner ? (
          <div className="glass-card space-y-3">
            <h3 className="font-bold text-sm flex items-center gap-2">
              <Pencil className="w-4 h-4 text-primary" /> Edit Campaign
            </h3>

            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Title</label>
              <input
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                maxLength={100}
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Description</label>
              <textarea
                value={editDescription}
                onChange={e => setEditDescription(e.target.value)}
                className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-y min-h-[120px]"
                rows={6}
                maxLength={5000}
              />
              <p className="text-[10px] text-muted-foreground mt-1 text-right">{editDescription.length}/5,000</p>
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Cover Image</label>
              {editImage ? (
                <div className="relative">
                  <img src={editImage} alt="" className="w-full h-36 rounded-xl object-cover border border-border" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  <button onClick={() => setEditImage('')} className="absolute top-2 right-2 p-1 bg-black/60 rounded-full text-white hover:bg-black/80">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div>
                  <input type="file" ref={editImageFileRef} accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    e.target.value = '';
                    setEditImageUploading(true);
                    try {
                      const url = await uploadImage(file);
                      setEditImage(url);
                    } catch {
                      setEditError('Image upload failed');
                    } finally {
                      setEditImageUploading(false);
                    }
                  }} />
                  <button
                    onClick={() => editImageFileRef.current?.click()}
                    disabled={editImageUploading}
                    className="w-full flex items-center justify-center gap-2 py-5 rounded-xl border-2 border-dashed border-border hover:border-primary/40 transition-colors"
                  >
                    {editImageUploading ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : <Upload className="w-4 h-4 text-muted-foreground" />}
                    <span className="text-xs text-muted-foreground">{editImageUploading ? 'Uploading...' : 'Upload Image'}</span>
                  </button>
                  {!editImageUploading && (
                    <input
                      className="w-full bg-muted rounded-lg px-3 py-2 text-xs mt-2 focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder="Or paste image URL..."
                      onPaste={e => {
                        const url = e.clipboardData.getData('text').trim();
                        if (url.startsWith('http')) { e.preventDefault(); setEditImage(url); }
                      }}
                      onChange={e => {
                        const url = e.target.value.trim();
                        if (url.startsWith('http')) setEditImage(url);
                      }}
                    />
                  )}
                </div>
              )}
            </div>

            {editError && <p className="text-xs text-red-500">{editError}</p>}

            <div className="flex gap-2 pt-1">
              <button
                onClick={() => { setEditMode(false); setEditTitle(data.title); setEditDescription(data.description); setEditImage(data.image || ''); setEditError(null); }}
                disabled={editSaving}
                className="flex-1 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={editSaving || !editTitle.trim()}
                className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        ) : (
        /* Campaign header */
        <div className="glass-card">
          {data.image && (
            <div className="-m-4 mb-4 rounded-t-xl overflow-hidden">
              <img src={data.image} alt="" className="w-full h-48 object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>
          )}
          <div className="flex items-center gap-2 mb-2">
            <StatusBadge status={status} />
            <h2 className="text-lg font-bold">{data.title}</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4 whitespace-pre-wrap leading-relaxed">{data.description}</p>

          {/* Progress */}
          <div className="h-3 rounded-full bg-muted overflow-hidden mb-2">
            <div
              className={`h-full rounded-full transition-all ${status === 'funded' ? 'bg-emerald-500' : 'bg-primary'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-bold text-primary">{satsToUSD(raisedSats, bchPrice) || `${raisedSats.toLocaleString()} sats`}</span>
            <span className="text-sm text-muted-foreground">{pct}% of {satsToUSD(data.goalSats, bchPrice) || `${data.goalSats.toLocaleString()} sats`}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" /> {pledges.length} backer{pledges.length !== 1 ? 's' : ''}
              {onChainSats !== null && onChainSats > nostrRaisedSats && (
                <span className="ml-1 text-[10px] text-emerald-500 font-medium">+ QR donations</span>
              )}
            </span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {status === 'expired' ? 'Campaign ended' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}</span>
          </div>

          {/* Creator */}
          <Link to={`/profile/${campaign.event.pubkey}`} className="flex items-center gap-2 mt-4 pt-3 border-t border-border hover:opacity-80">
            {profile?.picture ? (
              <img src={profile.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
            ) : (
              <div className="w-6 h-6 rounded-full bg-primary/10" />
            )}
            <span className="text-xs font-medium">{profile?.name || campaign.event.pubkey.slice(0, 12)}</span>
          </Link>

          {/* Confirmation badge on completed campaigns */}
          {status === 'completed' && confirmationCount > 0 && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <CircleCheckBig className="w-4 h-4 text-emerald-500" />
              <span className="text-xs font-medium text-emerald-500">
                {confirmationCount} backer{confirmationCount !== 1 ? 's' : ''} confirmed delivery
              </span>
            </div>
          )}
        </div>
        )}

        {/* Status explanation — help users understand the lifecycle */}
        {status === 'funded' && (
          <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-3">
            <p className="text-xs text-emerald-600 dark:text-emerald-400 leading-relaxed">
              <strong>Goal reached!</strong> The creator can now start building. All backer contributions are preserved.
            </p>
          </div>
        )}
        {status === 'in_progress' && (
          <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-3">
            <p className="text-xs text-amber-600 dark:text-amber-400 leading-relaxed">
              <strong>Building!</strong> The creator is working on this project. Backers will be able to confirm delivery once it's complete.
            </p>
          </div>
        )}
        {status === 'completed' && (
          <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-3">
            <p className="text-xs text-emerald-600 dark:text-emerald-400 leading-relaxed">
              <strong>Delivered!</strong> The creator has marked this project complete. Backers can verify delivery below.
            </p>
          </div>
        )}

        {/* Creator Reputation */}
        <CreatorReputationWidget pubkey={campaign.event.pubkey} bchPrice={bchPrice} />

        {/* ── Stretch Goal (owner, funded campaigns) ── */}
        {status === 'funded' && isOwner && !showStretchGoal && (
          <button
            onClick={() => setShowStretchGoal(true)}
            className="glass-card w-full text-left border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 via-background to-primary/5 hover:border-emerald-500/50 transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0 group-hover:bg-emerald-500/20 transition-colors">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">Campaign fully funded!</p>
                <p className="text-xs text-muted-foreground mt-0.5">Set a stretch goal to keep the momentum going</p>
              </div>
              <span className="text-xs font-medium text-emerald-500 shrink-0">Set Goal →</span>
            </div>
          </button>
        )}

        {showStretchGoal && isOwner && (
          <div className="glass-card border-emerald-500/30 space-y-4">
            <h3 className="font-bold text-sm flex items-center gap-2">
              <TrendingUp className="w-4.5 h-4.5 text-emerald-500" /> Set Stretch Goal
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Your campaign hit its goal! Increase it to raise even more. The campaign will move back to Active so backers can keep pledging.
            </p>

            {/* Current vs new goal */}
            <div className="rounded-xl bg-muted/50 p-3 space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Current goal</span>
                <span className="font-bold font-mono">{satsToUSD(data.goalSats, bchPrice) || `${data.goalSats.toLocaleString()} sats`}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Raised so far</span>
                <span className="font-bold font-mono text-emerald-500">{satsToUSD(raisedSats, bchPrice) || `${raisedSats.toLocaleString()} sats`}</span>
              </div>
            </div>

            {/* New goal input */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">New Goal (USD)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={stretchGoalUSD}
                  onChange={e => setStretchGoalUSD(e.target.value)}
                  placeholder={bchPrice ? `Min ${((data.goalSats / 1e8) * bchPrice + 1).toFixed(0)}` : 'Enter amount'}
                  className="w-full bg-muted rounded-lg pl-8 pr-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
              {stretchGoalSats > 0 && (
                <div className="mt-1.5 text-[10px] space-y-0.5">
                  <div className="flex justify-between text-muted-foreground">
                    <span>New goal</span>
                    <span className="font-mono">{stretchGoalSats.toLocaleString()} sats</span>
                  </div>
                  {!stretchGoalValid && stretchGoalSats > 0 && (
                    <p className="text-red-500 font-medium">New goal must be higher than the current goal</p>
                  )}
                  {stretchGoalValid && (
                    <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-medium">
                      <span>Still needed</span>
                      <span className="font-mono">{satsToUSD(stretchGoalSats - raisedSats, bchPrice) || `${(stretchGoalSats - raisedSats).toLocaleString()} sats`}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Quick presets based on current goal */}
            <div className="flex gap-2 flex-wrap">
              {[1.5, 2, 3, 5].map(mult => {
                const presetSats = Math.round(data.goalSats * mult);
                const presetUsd = bchPrice ? ((presetSats / 1e8) * bchPrice).toFixed(0) : null;
                return (
                  <button
                    key={mult}
                    onClick={() => setStretchGoalUSD(presetUsd || String(presetSats))}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-colors ${
                      stretchGoalUSD === presetUsd
                        ? 'border-emerald-500 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                        : 'border-border hover:border-emerald-500/40 hover:bg-emerald-500/5'
                    }`}
                  >
                    {mult}x {presetUsd ? `($${presetUsd})` : ''}
                  </button>
                );
              })}
            </div>

            {/* Extend deadline */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
                <CalendarPlus className="w-3.5 h-3.5" /> Extend deadline
              </label>
              <select
                value={stretchExtendDays}
                onChange={e => setStretchExtendDays(Number(e.target.value))}
                className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
              >
                <option value={7}>+ 7 days</option>
                <option value={14}>+ 14 days</option>
                <option value={30}>+ 30 days</option>
                <option value={60}>+ 60 days</option>
                <option value={90}>+ 90 days</option>
              </select>
            </div>

            {stretchError && <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{stretchError}</p>}

            <div className="flex gap-2">
              <button
                onClick={() => { setShowStretchGoal(false); setStretchGoalUSD(''); setStretchError(null); }}
                disabled={stretchSaving}
                className="flex-1 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStretchGoal}
                disabled={stretchSaving || !stretchGoalValid}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-primary text-white text-sm font-bold hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {stretchSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
                {stretchSaving ? 'Updating...' : 'Set Stretch Goal'}
              </button>
            </div>
          </div>
        )}

        {/* Upvote section — verified users only */}
        <CampaignUpvote campaignId={campaign.id} campaignPubkey={campaign.event.pubkey} />

        {/* Share & Boost */}
        <ShareBoostSection campaign={campaign} bchPrice={bchPrice} />

        {/* Feature Campaign — only for campaign owner */}
        {status === 'active' && user?.pubkey === campaign.event.pubkey && (
          <FeatureCampaignSection campaignId={campaign.id} bchPrice={bchPrice} auctionState={auctionState} />
        )}

        {/* Pledge section */}
        {status === 'active' && user && (
          <div className="glass-card">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <Heart className="w-4 h-4 text-primary" /> Back this Campaign
            </h3>

            {pledgeSuccess ? (
              <div className="space-y-3">
                <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-4 text-center space-y-1">
                  <Check className="w-8 h-8 text-emerald-500 mx-auto" />
                  <p className="text-sm font-bold text-emerald-500">Pledge Sent!</p>
                  <p className="text-xs text-muted-foreground">Thank you for supporting this campaign.</p>
                </div>
                {pledgeViralNote && (
                  <div className="rounded-lg bg-primary/5 border border-primary/20 p-3 space-y-2">
                    <p className="text-xs font-semibold text-primary flex items-center gap-1.5">
                      <Megaphone className="w-3.5 h-3.5" /> Posted to your feed
                    </p>
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed">{pledgeViralNote}</p>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Amount input */}
                <div className="mb-3">
                  <label className="text-xs text-muted-foreground mb-1 block">Pledge amount (USD)</label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={pledgeAmount}
                        onChange={e => setPledgeAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full bg-muted rounded-lg pl-8 pr-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>
                  </div>
                  {pledgeSats > 0 && (
                    <div className="mt-2 text-[10px] text-muted-foreground space-y-0.5">
                      <div className="flex justify-between"><span>Pledge</span><span className="font-mono">{pledgeSats.toLocaleString()} sats</span></div>
                      <div className="flex justify-between"><span>Platform fee (3%)</span><span className="font-mono">-{breakdown.fee.toLocaleString()} sats</span></div>
                      <div className="flex justify-between font-medium text-foreground"><span>Creator receives</span><span className="font-mono">{breakdown.net.toLocaleString()} sats</span></div>
                    </div>
                  )}
                </div>

                {/* Preset amounts */}
                <div className="flex gap-2 mb-3 flex-wrap">
                  {[1, 5, 10, 25, 50].map(amt => (
                    <button
                      key={amt}
                      onClick={() => setPledgeAmount(String(amt))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        pledgeAmount === String(amt) ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:border-primary/40'
                      }`}
                    >
                      ${amt}
                    </button>
                  ))}
                </div>

                {/* Wallet */}
                {!tipWallet.ready ? (
                  <button
                    onClick={handleInitWallet}
                    disabled={walletInitializing}
                    className="w-full py-2.5 rounded-lg bg-muted text-sm font-medium flex items-center justify-center gap-2"
                  >
                    {walletInitializing ? <div className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /> : <Wallet className="w-4 h-4" />}
                    {walletInitializing ? 'Loading Wallet...' : 'Load Wallet to Pledge'}
                  </button>
                ) : (
                  <>
                    <div className="text-[10px] text-muted-foreground mb-2 flex justify-between">
                      <span>Wallet balance</span>
                      <span className="font-mono">{satsToUSD(Number(tipWallet.balanceSats), bchPrice) || `${Number(tipWallet.balanceSats).toLocaleString()} sats`}</span>
                    </div>
                    <button
                      onClick={handlePledge}
                      disabled={pledging || pledgeSats <= 0 || Number(tipWallet.balanceSats) < pledgeSats}
                      className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 ${
                        pledgeSats > 0 && Number(tipWallet.balanceSats) >= pledgeSats
                          ? 'bg-gradient-to-r from-primary to-emerald-500 text-white hover:opacity-90'
                          : 'bg-muted text-muted-foreground cursor-not-allowed'
                      } disabled:opacity-50`}
                    >
                      {pledging ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Heart className="w-4 h-4" />}
                      {pledging ? 'Sending...' : pledgeSats > 0 ? `Pledge ${satsToUSD(pledgeSats, bchPrice) || `${pledgeSats.toLocaleString()} sats`}` : 'Enter an amount'}
                    </button>
                  </>
                )}
                {pledgeError && <p className="text-xs text-red-500 mt-2">{pledgeError}</p>}
              </>
            )}
          </div>
        )}

        {status === 'active' && !user && (
          <div className="glass-card text-center py-4">
            <p className="text-xs text-muted-foreground mb-2">Log in to back this campaign with your BCH wallet</p>
            <button onClick={login} className="px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium">Log In</button>
          </div>
        )}

        {/* QR Donate Card — visible to everyone, no login needed */}
        {data.bchAddress && (
          <CampaignQRCard
            bchAddress={data.bchAddress}
            campaignTitle={data.title}
            bchPrice={bchPrice}
            goalSats={data.goalSats}
            campaignId={campaign.id}
            campaignPubkey={campaign.event.pubkey}
            onPledgeConfirmed={() => setTimeout(() => refetchPledges(), 1500)}
          />
        )}

        {/* ── Creator Update Form ── */}
        {showUpdateForm && isOwner && (
          <div className="glass-card border-primary/30 space-y-3">
            <h3 className="font-bold text-sm flex items-center gap-2">
              <MessageSquarePlus className="w-4 h-4 text-primary" /> Post Campaign Update
            </h3>
            <p className="text-xs text-muted-foreground">Share progress with your backers. Updates appear in the campaign timeline and notify supporters.</p>
            <textarea
              value={updateContent}
              onChange={e => setUpdateContent(e.target.value)}
              placeholder="What's new with this project? Share progress, milestones, or any news..."
              className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-y min-h-[100px]"
              rows={4}
              maxLength={2000}
            />
            <p className="text-[10px] text-muted-foreground text-right">{updateContent.length}/2,000</p>
            {/* Image upload */}
            {updateImages.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {updateImages.map((url, i) => (
                  <div key={i} className="relative">
                    <img src={url} alt="" className="w-20 h-20 rounded-lg object-cover border border-border" />
                    <button onClick={() => setUpdateImages(prev => prev.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 p-0.5 bg-red-500 rounded-full text-white">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input type="file" ref={updateImageRef} accept="image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                e.target.value = '';
                setUpdateImageUploading(true);
                try {
                  const url = await uploadImage(file);
                  setUpdateImages(prev => [...prev, url]);
                } catch {}
                setUpdateImageUploading(false);
              }} />
              <button
                onClick={() => updateImageRef.current?.click()}
                disabled={updateImageUploading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs hover:bg-muted transition-colors"
              >
                {updateImageUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                {updateImageUploading ? 'Uploading...' : 'Add Photo'}
              </button>
            </div>
            {updateSuccess && (
              <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <Check className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-medium text-emerald-500">Update posted!</span>
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => { setShowUpdateForm(false); setUpdateContent(''); setUpdateImages([]); }} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-muted">Cancel</button>
              <button
                onClick={handlePostUpdate}
                disabled={publishingUpdate || !updateContent.trim()}
                className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {publishingUpdate ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquarePlus className="w-4 h-4" />}
                {publishingUpdate ? 'Posting...' : 'Post Update'}
              </button>
            </div>
          </div>
        )}

        {/* ── Completion Form ── */}
        {showCompletionForm && isOwner && (
          <div className="glass-card border-emerald-500/30 space-y-3">
            <h3 className="font-bold text-sm flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Mark Campaign Complete
            </h3>
            <p className="text-xs text-muted-foreground">Share proof of completion so backers can verify the project was delivered. This builds trust and reputation.</p>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">What was accomplished? *</label>
              <textarea
                value={completionDesc}
                onChange={e => setCompletionDesc(e.target.value)}
                placeholder="Describe what was delivered, how funds were used, and the outcome..."
                className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-y min-h-[100px]"
                rows={4}
                maxLength={2000}
              />
            </div>
            {/* Completion images */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Proof Photos (optional)</label>
              {completionImages.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2">
                  {completionImages.map((url, i) => (
                    <div key={i} className="relative">
                      <img src={url} alt="" className="w-24 h-24 rounded-lg object-cover border border-border" />
                      <button onClick={() => setCompletionImages(prev => prev.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 p-0.5 bg-red-500 rounded-full text-white">
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input type="file" ref={completionImageRef} accept="image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                e.target.value = '';
                setCompletionImageUploading(true);
                try {
                  const url = await uploadImage(file);
                  setCompletionImages(prev => [...prev, url]);
                } catch {}
                setCompletionImageUploading(false);
              }} />
              <button
                onClick={() => completionImageRef.current?.click()}
                disabled={completionImageUploading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs hover:bg-muted transition-colors"
              >
                {completionImageUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                {completionImageUploading ? 'Uploading...' : 'Add Photo'}
              </button>
            </div>
            {/* Links */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Related Links (optional, one per line)</label>
              <textarea
                value={completionLinks}
                onChange={e => setCompletionLinks(e.target.value)}
                placeholder="https://example.com/proof-photo&#10;https://example.com/demo-video"
                className="w-full bg-muted rounded-lg px-3 py-2.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-y"
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setShowCompletionForm(false); setCompletionDesc(''); setCompletionImages([]); setCompletionLinks(''); }} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-muted">Cancel</button>
              <button
                onClick={handleCompleteCampaign}
                disabled={completingCampaign || !completionDesc.trim()}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-primary text-white text-sm font-bold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {completingCampaign ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {completingCampaign ? 'Completing...' : 'Mark Complete'}
              </button>
            </div>
          </div>
        )}

        {/* ── Completion Proof Display ── */}
        {data.completionProof && (
          <div className="glass-card border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 via-background to-primary/5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
              </div>
              <div>
                <h3 className="font-bold text-sm text-emerald-600 dark:text-emerald-400">Completion Proof</h3>
                <p className="text-[10px] text-muted-foreground">Submitted by creator {data.completionProof.completedAt ? timeAgo(data.completionProof.completedAt) : ''}</p>
              </div>
            </div>
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed mb-3">{data.completionProof.description}</p>
            {data.completionProof.images && data.completionProof.images.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-3">
                {data.completionProof.images.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block">
                    <img src={url} alt="" className="w-28 h-28 rounded-xl object-cover border border-border hover:opacity-80 transition-opacity" />
                  </a>
                ))}
              </div>
            )}
            {data.completionProof.links && data.completionProof.links.length > 0 && (
              <div className="space-y-1">
                {data.completionProof.links.map((link, i) => (
                  <a key={i} href={link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-primary hover:underline">
                    <LinkIcon className="w-3 h-3" />
                    <span className="truncate">{link.replace(/^https?:\/\//, '')}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Community Confirmation ── */}
        {(status === 'completed' || status === 'in_progress') && (
          <div className="glass-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <CircleCheckBig className="w-4 h-4 text-emerald-500" /> Community Verification
              </h3>
              {confirmationCount > 0 && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 text-[10px] font-bold">
                  <Check className="w-3 h-3" /> {confirmationCount} confirmed
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              {status === 'completed'
                ? 'Did the creator deliver? Backers can confirm this project was completed as promised.'
                : 'The creator is working on this project. Backers can confirm delivery once complete.'
              }
            </p>
            {!user ? (
              <button onClick={login} className="w-full py-2.5 rounded-xl bg-muted text-sm font-medium flex items-center justify-center gap-2">
                Log in to verify
              </button>
            ) : hasConfirmed || confirmSuccess ? (
              <div className="w-full py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-sm font-bold flex items-center justify-center gap-2">
                <Check className="w-4 h-4" /> You confirmed delivery!
              </div>
            ) : isBackerUser ? (
              <button
                onClick={handleConfirmDelivery}
                disabled={confirming}
                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-primary text-white text-sm font-bold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CircleCheckBig className="w-4 h-4" />}
                {confirming ? 'Confirming...' : 'Confirm Delivery'}
              </button>
            ) : (
              <div className="w-full py-2.5 rounded-xl bg-muted text-muted-foreground text-sm font-medium text-center">
                Only backers can confirm delivery
              </div>
            )}
          </div>
        )}

        {/* ── Star Rating ── */}
        <CampaignRatingSection campaignId={campaign.id} campaignPubkey={campaign.event.pubkey} />

        {/* ── Campaign Timeline ── */}
        {timeline.length > 0 && (
          <div className="glass-card">
            <button
              onClick={() => setShowTimeline(!showTimeline)}
              className="w-full flex items-center justify-between"
            >
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <History className="w-4 h-4 text-primary" /> Campaign Timeline
                <span className="text-[10px] text-muted-foreground font-normal">({timeline.length} events)</span>
              </h3>
              {showTimeline ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>
            {showTimeline && (
              <div className="mt-3 space-y-0">
                {timeline.map((entry, i) => (
                  <TimelineRow key={`${entry.type}-${entry.timestamp}-${i}`} entry={entry} isLast={i === timeline.length - 1} bchPrice={bchPrice} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Creator Updates ── */}
        {campaignUpdates.length > 0 && (
          <div className="glass-card">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <MessageSquarePlus className="w-4 h-4 text-primary" /> Creator Updates ({campaignUpdates.length})
            </h3>
            <div className="space-y-3">
              {campaignUpdates.sort((a, b) => b.createdAt - a.createdAt).map(update => (
                <CampaignUpdateCard key={update.eventId} update={update} />
              ))}
            </div>
          </div>
        )}

        {/* Backers list */}
        {pledges.length > 0 && (
          <div className="glass-card">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-primary" /> Backers ({pledges.length})
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {pledges.sort((a, b) => b.created_at - a.created_at).map(ev => (
                <BackerRow key={ev.id} event={ev} bchPrice={bchPrice} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Share & Boost Section ─────────────────────────────────────────
function ShareBoostSection({ campaign, bchPrice }: { campaign: { event: any; data: CampaignData; id: string }; bchPrice: number | null }) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const tipWallet = useTipWallet();
  const { data } = campaign;
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);
  const [showBoost, setShowBoost] = useState(false);
  const [selectedTier, setSelectedTier] = useState(BOOST_TIERS[0].id);
  const [boosting, setBoosting] = useState(false);
  const [boostError, setBoostError] = useState<string | null>(null);
  const [boostSuccess, setBoostSuccess] = useState(false);
  const [walletInitializing, setWalletInitializing] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const tier = BOOST_TIERS.find(t => t.id === selectedTier)!;
  const tierSats = usdToSats(tier.priceUsd, bchPrice);
  const walletBalance = Number(tipWallet.balanceSats);
  const canAfford = tierSats !== null && walletBalance >= tierSats;

  const campaignUrl = `https://bchnostr.com/crowdfund?campaign=${campaign.id}`;

  const handleShare = async () => {
    if (!user) return;
    setSharing(true);
    try {
      const goalUsd = satsToUSD(data.goalSats, bchPrice);
      const imageLine = data.image ? `\n\n${data.image}` : '';
      await publish({
        kind: 1,
        content: `\uD83D\uDCE2 Check out this crowdfunding campaign on BCHNostr!\n\n"${data.title}"\n\n${data.description?.slice(0, 150)}${data.description && data.description.length > 150 ? '...' : ''}\n\nGoal: ${goalUsd || `${data.goalSats.toLocaleString()} sats`}\n\nBack it with BCH at ${campaignUrl}${imageLine}\n\n#bch #bitcoincash #crowdfunding`,
        tags: [
          ['t', 'bch'],
          ['t', 'bitcoincash'],
          ['t', 'crowdfunding'],
          ['e', campaign.id],
          ...(data.image ? [['imeta', `url ${data.image}`, 'm image/*']] : []),
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setShared(true);
      setTimeout(() => setShared(false), 3000);
    } catch (e) {
      console.error('Share failed:', e);
    } finally {
      setSharing(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(campaignUrl).catch(() => {});
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: data.title, text: data.description?.slice(0, 100), url: campaignUrl }).catch(() => {});
    } else {
      handleCopyLink();
    }
  };

  const handleInitWallet = async () => {
    setWalletInitializing(true);
    try { await tipWallet.init(); } catch {}
    setWalletInitializing(false);
  };

  const handleBoost = async () => {
    if (!user || !canAfford || !tierSats) return;
    setBoosting(true);
    setBoostError(null);
    try {
      const txId = await tipWallet.sendBCH(BOOST_WALLET, BigInt(tierSats));
      const now = Math.floor(Date.now() / 1000);
      const boostData: BoostData = {
        eventId: campaign.id,
        tier: tier.id,
        priceSats: tierSats,
        expiresAt: now + tier.duration,
        boostedBy: user.pubkey,
      };

      await publish({
        kind: BOOST_KIND,
        content: JSON.stringify(boostData),
        tags: [
          ['d', BOOST_D_TAG_PREFIX + campaign.id],
          ['t', BOOST_TAG],
          ['t', 'bch'],
          ['e', campaign.id],
          ['expires', String(now + tier.duration)],
          ['amount', String(tierSats)],
          ['txid', txId],
        ],
        created_at: now,
      });

      setBoostSuccess(true);
      tipWallet.refreshBalance();
    } catch (e) {
      setBoostError(e instanceof Error ? e.message : 'Boost failed');
    } finally {
      setBoosting(false);
    }
  };

  return (
    <div className="glass-card">
      <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
        <Megaphone className="w-4 h-4 text-primary" /> Share & Promote
      </h3>

      {/* Share buttons row */}
      <div className="flex gap-2 mb-3">
        {user && (
          <button
            onClick={handleShare}
            disabled={sharing || shared}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors ${
              shared ? 'bg-emerald-500 text-white' : 'bg-primary text-primary-foreground hover:bg-primary/90'
            } disabled:opacity-70`}
          >
            {sharing ? (
              <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : shared ? (
              <Check className="w-3.5 h-3.5" />
            ) : (
              <Share2 className="w-3.5 h-3.5" />
            )}
            {sharing ? 'Posting...' : shared ? 'Shared!' : 'Share on Nostr'}
          </button>
        )}
        <button
          onClick={handleNativeShare}
          className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-medium border border-border hover:bg-muted transition-colors"
        >
          {linkCopied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
          {linkCopied ? 'Copied!' : 'Copy Link'}
        </button>
      </div>

      {/* Boost to main feed — open to any logged-in user */}
      {user && !boostSuccess && (
        <>
          <button
            onClick={() => setShowBoost(!showBoost)}
            className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-amber-500/20 bg-amber-500/5 hover:bg-amber-500/10 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Rocket className="w-4 h-4 text-amber-500" />
              <div className="text-left">
                <p className="text-xs font-semibold">
                  {user.pubkey === campaign.event.pubkey ? 'Boost your campaign to the main feed' : 'Boost this campaign to the main feed'}
                </p>
                <p className="text-[10px] text-muted-foreground">Pin it at the top of the BCHNostr home feed</p>
              </div>
            </div>
            <span className="text-muted-foreground text-xs">{showBoost ? '\u25B2' : '\u25BC'}</span>
          </button>

          {showBoost && (
            <div className="mt-3 space-y-2">
              {/* Tier selection */}
              {BOOST_TIERS.map(t => {
                const tSats = usdToSats(t.priceUsd, bchPrice);
                return (
                  <button
                    key={t.id}
                    onClick={() => setSelectedTier(t.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border transition-all ${
                      selectedTier === t.id
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                        : 'border-border hover:border-primary/40'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{t.emoji}</span>
                      <div className="text-left">
                        <p className="text-xs font-semibold">{t.label}</p>
                        <p className="text-[10px] text-muted-foreground">Pinned on main feed</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-bold font-mono">${t.priceUsd.toFixed(2)}</span>
                      <p className="text-[9px] text-muted-foreground font-mono">
                        {tSats ? `~${tSats.toLocaleString()} sats` : '...'}
                      </p>
                    </div>
                  </button>
                );
              })}

              {/* Wallet + boost button */}
              {!tipWallet.ready ? (
                <button
                  onClick={handleInitWallet}
                  disabled={walletInitializing}
                  className="w-full py-2.5 rounded-lg bg-muted text-sm font-medium flex items-center justify-center gap-2"
                >
                  {walletInitializing ? <div className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /> : <Wallet className="w-4 h-4" />}
                  {walletInitializing ? 'Loading...' : 'Load Wallet to Boost'}
                </button>
              ) : (
                <>
                  <div className="text-[10px] text-muted-foreground flex justify-between px-1">
                    <span>Wallet balance</span>
                    <span className={`font-mono font-bold ${canAfford ? 'text-emerald-500' : 'text-red-500'}`}>
                      {satsToUSD(walletBalance, bchPrice) || `${walletBalance.toLocaleString()} sats`}
                    </span>
                  </div>
                  <button
                    onClick={handleBoost}
                    disabled={!canAfford || boosting}
                    className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 ${
                      canAfford ? 'bg-gradient-to-r from-primary to-amber-500 text-white hover:opacity-90' : 'bg-muted text-muted-foreground cursor-not-allowed'
                    } disabled:opacity-50`}
                  >
                    {boosting ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Rocket className="w-4 h-4" />}
                    {boosting ? 'Boosting...' : `Boost for $${tier.priceUsd.toFixed(2)}`}
                  </button>
                  {!canAfford && <p className="text-[10px] text-red-500">Not enough funds. Deposit to your tip wallet in Settings.</p>}
                </>
              )}
              {boostError && <p className="text-xs text-red-500">{boostError}</p>}
            </div>
          )}
        </>
      )}

      {boostSuccess && (
        <div className="mt-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-3 text-center space-y-1">
          <Rocket className="w-6 h-6 text-emerald-500 mx-auto" />
          <p className="text-xs font-bold text-emerald-500">Campaign Boosted!</p>
          <p className="text-[10px] text-muted-foreground">This campaign will appear at the top of the BCHNostr main feed for {tier.label.toLowerCase()}.</p>
        </div>
      )}
    </div>
  );
}

function BackerRow({ event, bchPrice }: { event: any; bchPrice: number | null }) {
  const { profile } = useNostrProfile(event.pubkey);
  const amountTag = event.tags.find((t: string[]) => t[0] === 'amount');
  const amount = amountTag ? parseInt(amountTag[1]) || 0 : 0;

  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
      <Link to={`/profile/${event.pubkey}`} className="shrink-0">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-primary/10" />
        )}
      </Link>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate">{profile?.name || event.pubkey.slice(0, 10)}</p>
        <p className="text-[10px] text-muted-foreground">{timeAgo(event.created_at)}</p>
      </div>
      <span className="text-xs font-bold text-primary">{satsToUSD(amount, bchPrice) || `${amount.toLocaleString()} sats`}</span>
    </div>
  );
}

// ── Star Rating Display ──────────────────────────────────────────
function StarRatingDisplay({ rating, size = 'sm', showNumber = false }: { rating: number; size?: 'xs' | 'sm' | 'md'; showNumber?: boolean }) {
  const starSize = size === 'xs' ? 'w-3 h-3' : size === 'sm' ? 'w-3.5 h-3.5' : 'w-4.5 h-4.5';
  const textSize = size === 'xs' ? 'text-[10px]' : size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => {
        const filled = rating >= i;
        const half = !filled && rating >= i - 0.5;
        return (
          <span key={i} className="relative">
            <Star className={`${starSize} ${filled ? 'text-amber-400 fill-amber-400' : half ? 'text-amber-400' : 'text-muted-foreground/30'}`} />
            {half && (
              <span className="absolute inset-0 overflow-hidden w-[50%]">
                <Star className={`${starSize} text-amber-400 fill-amber-400`} />
              </span>
            )}
          </span>
        );
      })}
      {showNumber && rating > 0 && (
        <span className={`${textSize} font-bold text-amber-600 dark:text-amber-400 ml-1`}>{rating.toFixed(1)}</span>
      )}
    </div>
  );
}

// ── Star Rating Input ────────────────────────────────────────────
function StarRatingInput({ value, onChange, disabled }: { value: number; onChange: (v: number) => void; disabled?: boolean }) {
  const [hover, setHover] = useState(0);

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type="button"
          disabled={disabled}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(i)}
          className="p-0.5 transition-transform hover:scale-110 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Star className={`w-6 h-6 transition-colors ${
            (hover || value) >= i
              ? 'text-amber-400 fill-amber-400'
              : 'text-muted-foreground/30 hover:text-amber-300'
          }`} />
        </button>
      ))}
      {(hover || value) > 0 && (
        <span className="text-xs font-medium text-amber-600 dark:text-amber-400 ml-1.5">
          {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][hover || value]}
        </span>
      )}
    </div>
  );
}

// ── Campaign Rating Section ──────────────────────────────────────
function CampaignRatingSection({ campaignId, campaignPubkey }: { campaignId: string; campaignPubkey: string }) {
  const { user, login } = useAppContext();
  const { publish } = useNostrPublish();
  const [myRating, setMyRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Fetch all ratings for this campaign
  const { events: ratingEvents, refetch } = useNostrQuery({
    filters: [{ kinds: [RATING_KIND], '#e': [campaignId], '#t': [RATING_TAG], limit: 500 }],
    enabled: true,
  });

  const { ratings, avgRating, userExistingRating } = useMemo(() => {
    const parsed: CampaignRating[] = [];
    ratingEvents.forEach(ev => {
      const r = parseRating(ev);
      if (r) parsed.push(r);
    });
    const avg = computeAverageRating(parsed, campaignPubkey);
    // Find user's latest rating
    let existingRating = 0;
    if (user) {
      const userRatings = parsed.filter(r => r.pubkey === user.pubkey);
      if (userRatings.length > 0) {
        existingRating = userRatings.sort((a, b) => b.createdAt - a.createdAt)[0].rating;
      }
    }
    return { ratings: parsed, avgRating: avg, userExistingRating: existingRating };
  }, [ratingEvents, user]);

  // Set initial value from existing rating
  useEffect(() => {
    if (userExistingRating > 0 && myRating === 0) {
      setMyRating(userExistingRating);
    }
  }, [userExistingRating]);

  const handleSubmitRating = async () => {
    if (!user || myRating === 0 || submitting) return;
    setSubmitting(true);
    try {
      await publish({
        kind: RATING_KIND,
        content: '',
        tags: [
          ['e', campaignId],
          ['p', campaignPubkey],
          ['t', RATING_TAG],
          ['rating', String(myRating)],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setSubmitted(true);
      setTimeout(() => refetch(), 2000);
    } catch (e) {
      console.error('Rating failed:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const hasRated = userExistingRating > 0 || submitted;
  const isOwnCampaign = user?.pubkey === campaignPubkey;

  return (
    <div className="glass-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-400 fill-amber-400" /> Campaign Rating
        </h3>
        {avgRating.count > 0 && (
          <div className="flex items-center gap-1.5">
            <StarRatingDisplay rating={avgRating.average} size="sm" />
            <span className="text-xs text-muted-foreground font-medium">{avgRating.average.toFixed(1)} ({avgRating.count})</span>
          </div>
        )}
      </div>

      {avgRating.count === 0 && (
        <p className="text-xs text-muted-foreground mb-3">No ratings yet. Be the first to rate this campaign!</p>
      )}

      {/* Rating distribution */}
      {avgRating.count >= 3 && (
        <RatingDistribution ratings={ratings} creatorPubkey={campaignPubkey} />
      )}

      {/* User rating input */}
      {!user ? (
        <button onClick={login} className="w-full py-2.5 rounded-xl bg-muted text-sm font-medium flex items-center justify-center gap-2 mt-2">
          Log in to rate
        </button>
      ) : isOwnCampaign ? (
        <div className="flex items-center gap-2 mt-3 py-2 px-3 rounded-lg bg-muted/50 border border-border">
          <span className="text-xs text-muted-foreground">You can't rate your own campaign</span>
        </div>
      ) : hasRated ? (
        <div className="flex items-center gap-2 mt-3 py-2 px-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <Check className="w-4 h-4 text-amber-500" />
          <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
            You rated this {userExistingRating || myRating} star{(userExistingRating || myRating) !== 1 ? 's' : ''}
          </span>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-muted-foreground">Rate this campaign:</p>
          <div className="flex items-center gap-3">
            <StarRatingInput value={myRating} onChange={setMyRating} disabled={submitting} />
            {myRating > 0 && (
              <button
                onClick={handleSubmitRating}
                disabled={submitting}
                className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-bold hover:bg-amber-600 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {submitting ? 'Saving...' : 'Submit'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Rating Distribution Bar ──────────────────────────────────────
function RatingDistribution({ ratings, creatorPubkey }: { ratings: CampaignRating[]; creatorPubkey?: string }) {
  // Deduplicate (latest per user), exclude creator self-ratings
  const byUser = new Map<string, CampaignRating>();
  for (const r of ratings) {
    if (creatorPubkey && r.pubkey === creatorPubkey) continue;
    const existing = byUser.get(r.pubkey);
    if (!existing || r.createdAt > existing.createdAt) byUser.set(r.pubkey, r);
  }
  const unique = [...byUser.values()];
  const total = unique.length;
  const counts = [0, 0, 0, 0, 0]; // index 0 = 1 star, index 4 = 5 stars
  unique.forEach(r => { if (r.rating >= 1 && r.rating <= 5) counts[r.rating - 1]++; });

  return (
    <div className="space-y-1 mb-3">
      {[5, 4, 3, 2, 1].map(star => {
        const count = counts[star - 1];
        const pct = total > 0 ? (count / total) * 100 : 0;
        return (
          <div key={star} className="flex items-center gap-2 text-[10px]">
            <span className="w-3 text-right text-muted-foreground font-medium">{star}</span>
            <Star className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />
            <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full bg-amber-400 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-4 text-right text-muted-foreground">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Timeline Row ──────────────────────────────────────────────────
function TimelineRow({ entry, isLast, bchPrice }: { entry: TimelineEntry; isLast: boolean; bchPrice: number | null }) {
  const { profile } = useNostrProfile(entry.pubkey);

  const iconMap: Record<string, React.ReactNode> = {
    created: <Plus className="w-3 h-3 text-primary" />,
    pledge: <Heart className="w-3 h-3 text-pink-500" />,
    milestone: <TrendingUp className="w-3 h-3 text-amber-500" />,
    funded: <Target className="w-3 h-3 text-emerald-500" />,
    update: <MessageSquarePlus className="w-3 h-3 text-blue-500" />,
    status_change: <Construction className="w-3 h-3 text-amber-500" />,
    completed: <CheckCircle2 className="w-3 h-3 text-emerald-500" />,
    confirmed: <CircleCheckBig className="w-3 h-3 text-emerald-500" />,
  };

  const bgMap: Record<string, string> = {
    created: 'bg-primary/10 border-primary/20',
    pledge: 'bg-pink-500/10 border-pink-500/20',
    milestone: 'bg-amber-500/10 border-amber-500/20',
    funded: 'bg-emerald-500/10 border-emerald-500/20',
    update: 'bg-blue-500/10 border-blue-500/20',
    status_change: 'bg-amber-500/10 border-amber-500/20',
    completed: 'bg-emerald-500/10 border-emerald-500/20',
    confirmed: 'bg-emerald-500/10 border-emerald-500/20',
  };

  return (
    <div className="flex gap-3">
      {/* Timeline line + dot */}
      <div className="flex flex-col items-center">
        <div className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${bgMap[entry.type] || 'bg-muted border-border'}`}>
          {iconMap[entry.type] || <Clock className="w-3 h-3 text-muted-foreground" />}
        </div>
        {!isLast && <div className="w-px flex-1 bg-border min-h-[16px]" />}
      </div>
      {/* Content */}
      <div className={`flex-1 min-w-0 ${isLast ? 'pb-0' : 'pb-3'}`}>
        <p className="text-xs font-medium text-foreground leading-tight">{entry.description}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-muted-foreground">{timeAgo(entry.timestamp)}</span>
          {entry.pubkey && profile?.name && (
            <Link to={`/profile/${entry.pubkey}`} className="text-[10px] text-muted-foreground hover:text-foreground">
              by {profile.name}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Campaign Update Card ──────────────────────────────────────────
function CampaignUpdateCard({ update }: { update: CampaignUpdate }) {
  const { profile } = useNostrProfile(update.pubkey);

  return (
    <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Link to={`/profile/${update.pubkey}`} className="shrink-0">
          {profile?.picture ? (
            <img src={profile.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-primary/10" />
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <span className="text-xs font-medium">{profile?.name || update.pubkey.slice(0, 8)}</span>
          <span className="text-[10px] text-muted-foreground ml-2">{timeAgo(update.createdAt)}</span>
        </div>
      </div>
      <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{update.content}</p>
      {update.images && update.images.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {update.images.map((url, i) => (
            <a key={i} href={url} target="_blank" rel="noopener noreferrer">
              <img src={url} alt="" className="w-24 h-24 rounded-lg object-cover border border-border hover:opacity-80 transition-opacity" />
            </a>
          ))}
        </div>
      )}
      {update.links && update.links.length > 0 && (
        <div className="space-y-1">
          {update.links.map((link, i) => (
            <a key={i} href={link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline">
              <LinkIcon className="w-3 h-3" />
              <span className="truncate">{link.replace(/^https?:\/\//, '')}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Creator Reputation Widget ────────────────────────────────────
// Reusable component to show creator reputation stats
export function CreatorReputationWidget({ pubkey, bchPrice }: { pubkey: string; bchPrice: number | null }) {
  const { profile } = useNostrProfile(pubkey);

  // Fetch all campaigns by this creator
  const { events: creatorCampaigns } = useNostrQuery({
    filters: [{ kinds: [CAMPAIGN_KIND], authors: [pubkey], '#t': [CAMPAIGN_TAG], limit: 100 }],
    enabled: !!pubkey,
  });

  // Parse campaign data
  const campaignList = useMemo(() => {
    return creatorCampaigns.map(ev => {
      try {
        const data: CampaignData = JSON.parse(ev.content);
        const dTag = ev.tags?.find((t: string[]) => t[0] === 'd')?.[1] || '';
        const aTag = `${CAMPAIGN_KIND}:${ev.pubkey}:${dTag}`;
        return { event: ev, data, id: ev.id, aTag };
      } catch { return null; }
    }).filter(Boolean) as { event: any; data: CampaignData; id: string; aTag: string }[];
  }, [creatorCampaigns]);

  // Fetch pledges for all creator campaigns (by both #e and #a for full coverage)
  const campaignIds = useMemo(() => campaignList.map(c => c.id), [campaignList]);
  const repCampaignATags = useMemo(() => campaignList.map(c => c.aTag), [campaignList]);
  const { events: allPledges } = useNostrQuery({
    filters: campaignIds.length > 0 ? [
      { kinds: [PLEDGE_KIND], '#e': campaignIds, limit: 2000 },
      ...(repCampaignATags.length > 0 ? [{ kinds: [PLEDGE_KIND], '#a': repCampaignATags, limit: 2000 }] : []),
    ] : [],
    enabled: campaignIds.length > 0,
  });

  // Fetch confirmations for all creator campaigns
  const { events: allConfirmations } = useNostrQuery({
    filters: campaignIds.length > 0 ? [{ kinds: [7], '#e': campaignIds, '#t': [CONFIRMATION_TAG], limit: 500 }] : [],
    enabled: campaignIds.length > 0,
  });

  // Fetch star ratings for all creator campaigns
  const { events: allRatingEventsRep } = useNostrQuery({
    filters: campaignIds.length > 0 ? [{ kinds: [RATING_KIND], '#e': campaignIds, '#t': [RATING_TAG], limit: 2000 }] : [],
    enabled: campaignIds.length > 0,
  });

  // Calculate reputation
  const reputation = useMemo(() => {
    if (campaignList.length === 0) return null;

    // Deduplicate pledges
    const seenPledgeIds = new Set<string>();
    const uniquePledges = allPledges.filter(ev => {
      if (seenPledgeIds.has(ev.id)) return false;
      seenPledgeIds.add(ev.id);
      return true;
    });

    const campaignData = campaignList.map(c => {
      const raisedSats = uniquePledges
        .filter(ev =>
          ev.tags.some((t: string[]) => t[0] === 'e' && t[1] === c.id) ||
          ev.tags.some((t: string[]) => t[0] === 'a' && t[1] === c.aTag)
        )
        .reduce((sum, ev) => {
          const amountTag = ev.tags.find((t: string[]) => t[0] === 'amount');
          return sum + (amountTag ? parseInt(amountTag[1]) || 0 : 0);
        }, 0);

      const confirmationCount = new Set(
        allConfirmations
          .filter(ev => ev.tags.some((t: string[]) => t[0] === 'e' && t[1] === c.id))
          .map(ev => ev.pubkey)
      ).size;

      return { data: c.data, raisedSats, confirmationCount };
    });

    // Compute creator-wide rating
    const allParsedRatings: CampaignRating[] = [];
    allRatingEventsRep.forEach(ev => {
      const r = parseRating(ev);
      if (r) allParsedRatings.push(r);
    });
    const ratingData = computeCreatorAverageRating(allParsedRatings, pubkey);

    return calculateReputation(campaignData, ratingData);
  }, [campaignList, allPledges, allConfirmations, allRatingEventsRep, pubkey]);

  if (!reputation || reputation.campaignsCreated === 0) return null;

  const satsToUSDLocal = (sats: number) => {
    if (!bchPrice || bchPrice <= 0) return null;
    const usd = (sats / 1e8) * bchPrice;
    if (usd < 0.01) return '<$0.01';
    return '$' + usd.toFixed(2);
  };

  return (
    <div className="glass-card border-primary/20">
      <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
        <Award className="w-4 h-4 text-primary" /> Creator Reputation
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="text-center p-2.5 rounded-xl bg-muted/50">
          <p className="text-lg font-bold text-foreground">{reputation.campaignsCreated}</p>
          <p className="text-[10px] text-muted-foreground">Campaigns Created</p>
        </div>
        <div className="text-center p-2.5 rounded-xl bg-muted/50">
          <p className="text-lg font-bold text-emerald-500">{reputation.campaignsCompleted}</p>
          <p className="text-[10px] text-muted-foreground">Completed</p>
        </div>
        <div className="text-center p-2.5 rounded-xl bg-muted/50">
          <p className="text-lg font-bold text-primary">
            {satsToUSDLocal(reputation.totalRaisedSats) || `${reputation.totalRaisedSats.toLocaleString()}s`}
          </p>
          <p className="text-[10px] text-muted-foreground">Total BCH Raised</p>
        </div>
        <div className="text-center p-2.5 rounded-xl bg-muted/50">
          <div className="flex items-center justify-center gap-1">
            <p className={`text-lg font-bold ${reputation.completionRate >= 75 ? 'text-emerald-500' : reputation.completionRate >= 50 ? 'text-amber-500' : 'text-red-500'}`}>
              {reputation.completionRate}%
            </p>
          </div>
          <p className="text-[10px] text-muted-foreground">Completion Rate</p>
        </div>
      </div>
      {/* Average Rating */}
      {reputation.averageRating > 0 && (
        <div className="mt-3 flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-amber-500/5 border border-amber-500/20">
          <StarRatingDisplay rating={reputation.averageRating} size="sm" showNumber />
          <span className="text-[10px] text-muted-foreground">from {reputation.totalRatings} rating{reputation.totalRatings !== 1 ? 's' : ''}</span>
        </div>
      )}
      {reputation.confirmations > 0 && (
        <div className="mt-2 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <CircleCheckBig className="w-3.5 h-3.5 text-emerald-500" />
          <span className="text-xs font-medium text-emerald-500">{reputation.confirmations} community confirmation{reputation.confirmations !== 1 ? 's' : ''}</span>
        </div>
      )}
    </div>
  );
}

// ── Campaign History for Profile ──────────────────────────────────
export function CampaignsTab({ pubkey, bchPrice }: { pubkey: string; bchPrice: number | null }) {
  // Fetch all campaigns by this creator
  const { events: creatorCampaigns, loading } = useNostrQuery({
    filters: [{ kinds: [CAMPAIGN_KIND], authors: [pubkey], '#t': [CAMPAIGN_TAG], limit: 100 }],
    enabled: !!pubkey,
    allRelays: true,
  });

  const campaignList = useMemo(() => {
    return creatorCampaigns.map(ev => {
      try {
        const data: CampaignData = JSON.parse(ev.content);
        return { event: ev, data, id: ev.id };
      } catch { return null; }
    }).filter(Boolean) as { event: any; data: CampaignData; id: string }[];
  }, [creatorCampaigns]);

  // Fetch pledges for all campaigns
  const campaignIds = useMemo(() => campaignList.map(c => c.id), [campaignList]);
  const { events: allPledges } = useNostrQuery({
    filters: campaignIds.length > 0 ? [{ kinds: [PLEDGE_KIND], '#e': campaignIds, limit: 2000 }] : [],
    enabled: campaignIds.length > 0,
  });

  // Fetch confirmations
  const { events: allConfirmations } = useNostrQuery({
    filters: campaignIds.length > 0 ? [{ kinds: [7], '#e': campaignIds, '#t': [CONFIRMATION_TAG], limit: 500 }] : [],
    enabled: campaignIds.length > 0,
  });

  // Build raised map
  const raisedMap = useMemo(() => {
    const map = new Map<string, number>();
    allPledges.forEach(ev => {
      const eTags = ev.tags.filter((t: string[]) => t[0] === 'e');
      const amountTag = ev.tags.find((t: string[]) => t[0] === 'amount');
      const amount = amountTag ? parseInt(amountTag[1]) || 0 : 0;
      eTags.forEach((t: string[]) => map.set(t[1], (map.get(t[1]) || 0) + amount));
    });
    return map;
  }, [allPledges]);

  // Build confirmation count map
  const confirmMap = useMemo(() => {
    const map = new Map<string, number>();
    allConfirmations.forEach(ev => {
      const eTags = ev.tags.filter((t: string[]) => t[0] === 'e');
      eTags.forEach((t: string[]) => map.set(t[1], (map.get(t[1]) || 0) + 1));
    });
    return map;
  }, [allConfirmations]);

  const satsToUSDLocal = (sats: number) => {
    if (!bchPrice || bchPrice <= 0) return null;
    const usd = (sats / 1e8) * bchPrice;
    if (usd < 0.01) return '<$0.01';
    return '$' + usd.toFixed(2);
  };

  if (loading && campaignList.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (campaignList.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <Target className="w-10 h-10 text-muted-foreground/20 mx-auto mb-2" />
        <p className="text-sm font-medium text-muted-foreground">No campaigns yet</p>
        <p className="text-xs text-muted-foreground mt-1">Crowdfunding campaigns will appear here.</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-border">
      {campaignList
        .sort((a, b) => b.event.created_at - a.event.created_at)
        .map(c => {
          const raised = raisedMap.get(c.id) || 0;
          const status = getCampaignStatus(c.data, raised);
          const pct = Math.min(100, Math.round((raised / c.data.goalSats) * 100));
          const confirms = confirmMap.get(c.id) || 0;

          return (
            <Link
              key={c.id}
              to={`/crowdfund?campaign=${c.id}`}
              className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
            >
              {c.data.image ? (
                <img src={c.data.image} alt="" className="w-12 h-12 rounded-lg object-cover border border-border shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Target className="w-5 h-5 text-primary/40" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <StatusBadge status={status} />
                  <p className="text-sm font-medium truncate">{c.data.title}</p>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-1">
                  <div
                    className={`h-full rounded-full ${status === 'completed' || status === 'funded' ? 'bg-emerald-500' : 'bg-primary'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                  <span className="font-medium text-primary">{satsToUSDLocal(raised) || `${raised.toLocaleString()}s`}</span>
                  <span>of {satsToUSDLocal(c.data.goalSats) || `${c.data.goalSats.toLocaleString()}s`}</span>
                  {confirms > 0 && (
                    <span className="flex items-center gap-0.5 text-emerald-500">
                      <CircleCheckBig className="w-2.5 h-2.5" /> {confirms}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
    </div>
  );
}

// ── Feature Campaign (Auction System) ────────────────────────────
function FeatureCampaignSection({ campaignId, bchPrice, auctionState }: {
  campaignId: string;
  bchPrice: number | null;
  auctionState: ReturnType<typeof computeAuctionState>;
}) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const tipWallet = useTipWallet();
  const [bidAmount, setBidAmount] = useState('');
  const [featuring, setFeaturing] = useState(false);
  const [featureError, setFeatureError] = useState<string | null>(null);
  const [featureSuccess, setFeatureSuccess] = useState(false);
  const [walletInitializing, setWalletInitializing] = useState(false);

  const { slotsUsed, slotsFull, minimumBid, lowestBid, bids } = auctionState;
  const slotsOpen = FEATURED_MAX_SLOTS - slotsUsed;

  // Already featured?
  const alreadyFeatured = bids.some(b => b.campaignId === campaignId);

  // Convert bid input (USD) to sats
  const bidUsd = parseFloat(bidAmount) || 0;
  const bidSats = bchPrice && bidUsd > 0 ? Math.round((bidUsd / bchPrice) * 1e8) : 0;
  const meetsMinimum = bidSats >= minimumBid;
  const walletBalance = Number(tipWallet.balanceSats);
  const canAfford = walletBalance >= bidSats && bidSats > 0;

  const handleInitWallet = async () => {
    setWalletInitializing(true);
    try { await tipWallet.init(); } catch {}
    setWalletInitializing(false);
  };

  const handleFeature = async () => {
    if (!user || !meetsMinimum || !canAfford) return;
    setFeaturing(true);
    setFeatureError(null);

    try {
      await tipWallet.sendBCH(PLATFORM_FEE_ADDRESS, BigInt(bidSats));

      const now = Math.floor(Date.now() / 1000);
      const featuredData: FeaturedCampaignData = {
        campaignId,
        tier: 'auction',
        priceSats: bidSats,
        expiresAt: now + FEATURED_AUCTION_DURATION,
        featuredBy: user.pubkey,
      };

      await publish({
        kind: FEATURED_CAMPAIGN_KIND,
        content: JSON.stringify(featuredData),
        tags: [
          ['d', FEATURED_CAMPAIGN_D_TAG_PREFIX + campaignId],
          ['t', FEATURED_CAMPAIGN_TAG],
          ['t', 'bch'],
          ['e', campaignId],
          ['expires', String(now + FEATURED_AUCTION_DURATION)],
          ['amount', String(bidSats)],
        ],
        created_at: now,
      });

      setFeatureSuccess(true);
      tipWallet.refreshBalance();
    } catch (e) {
      setFeatureError(e instanceof Error ? e.message : 'Bid failed');
    } finally {
      setFeaturing(false);
    }
  };

  if (featureSuccess) {
    return (
      <div className="glass-card border-amber-500/30 text-center py-5 space-y-2">
        <Star className="w-8 h-8 text-amber-500 mx-auto fill-amber-500" />
        <p className="text-sm font-bold text-amber-600 dark:text-amber-400">Campaign Featured!</p>
        <p className="text-xs text-muted-foreground">Your bid of {satsToUSD(bidSats, bchPrice) || `${bidSats.toLocaleString()} sats`} secures a featured slot for 7 days.</p>
      </div>
    );
  }

  if (alreadyFeatured) {
    return (
      <div className="glass-card border-amber-500/20 text-center py-4 space-y-1">
        <Star className="w-6 h-6 text-amber-500 mx-auto fill-amber-500" />
        <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">Your campaign is featured!</p>
        <p className="text-[11px] text-muted-foreground">It's currently in one of the top {FEATURED_MAX_SLOTS} slots.</p>
      </div>
    );
  }

  const minBidUsd = bchPrice ? (minimumBid / 1e8) * bchPrice : 0;

  return (
    <div className="glass-card border-amber-500/20 space-y-3">
      <h3 className="font-semibold text-sm flex items-center gap-2">
        <Star className="w-4 h-4 text-amber-500" /> Feature Your Campaign
      </h3>

      {/* Auction status */}
      <div className="rounded-xl bg-amber-500/5 border border-amber-500/10 p-3 space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Featured slots</span>
          <span className="font-bold">
            <span className={slotsOpen > 0 ? 'text-emerald-500' : 'text-amber-500'}>{slotsOpen}</span>
            <span className="text-muted-foreground"> of {FEATURED_MAX_SLOTS} open</span>
          </span>
        </div>
        {slotsFull && lowestBid && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Lowest current bid</span>
            <span className="font-bold font-mono text-amber-600 dark:text-amber-400">
              {satsToUSD(lowestBid.priceSats, bchPrice) || `${lowestBid.priceSats.toLocaleString()} sats`}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Minimum bid</span>
          <span className="font-bold font-mono">
            {satsToUSD(minimumBid, bchPrice) || `${minimumBid.toLocaleString()} sats`}
          </span>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground leading-relaxed">
        {slotsFull
          ? `All ${FEATURED_MAX_SLOTS} slots are taken. Outbid the lowest to claim a spot — they'll drop to Boosted visibility. Your bid lasts 7 days.`
          : `${slotsOpen} slot${slotsOpen !== 1 ? 's' : ''} available. Place a bid to feature your campaign at the top for 7 days.`
        }
      </p>

      {/* Bid input */}
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Your bid (USD)</label>
        <div className="relative">
          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={bidAmount}
            onChange={e => setBidAmount(e.target.value)}
            placeholder={minBidUsd > 0 ? `Min ${minBidUsd.toFixed(2)}` : '0.00'}
            className="w-full bg-muted rounded-lg pl-8 pr-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
        </div>
        {bidSats > 0 && (
          <div className="flex items-center justify-between mt-1.5 text-[10px]">
            <span className="text-muted-foreground font-mono">{bidSats.toLocaleString()} sats</span>
            {!meetsMinimum && (
              <span className="text-red-500 font-medium">
                Below minimum ({satsToUSD(minimumBid, bchPrice) || `${minimumBid.toLocaleString()} sats`})
              </span>
            )}
            {meetsMinimum && slotsFull && (
              <span className="text-emerald-500 font-medium">Outbids lowest slot</span>
            )}
            {meetsMinimum && !slotsFull && (
              <span className="text-emerald-500 font-medium">Slot available</span>
            )}
          </div>
        )}
      </div>

      {/* Quick bid presets */}
      <div className="flex gap-2 flex-wrap">
        {[
          { label: 'Min', sats: minimumBid },
          { label: '+50%', sats: Math.ceil(minimumBid * 1.5) },
          { label: '2x', sats: minimumBid * 2 },
          { label: '5x', sats: minimumBid * 5 },
        ].map(preset => {
          const usd = bchPrice ? ((preset.sats / 1e8) * bchPrice).toFixed(2) : null;
          return (
            <button
              key={preset.label}
              onClick={() => setBidAmount(usd || String(preset.sats))}
              className="px-3 py-1.5 rounded-lg text-[11px] font-medium border border-border hover:border-amber-500/40 hover:bg-amber-500/5 transition-colors"
            >
              {preset.label} {usd ? `($${usd})` : `(${preset.sats.toLocaleString()}s)`}
            </button>
          );
        })}
      </div>

      {/* Wallet + submit */}
      {!tipWallet.ready ? (
        <button
          onClick={handleInitWallet}
          disabled={walletInitializing}
          className="w-full py-2.5 rounded-lg bg-muted text-sm font-medium flex items-center justify-center gap-2"
        >
          {walletInitializing ? <div className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /> : <Wallet className="w-4 h-4" />}
          {walletInitializing ? 'Loading Wallet...' : 'Load Wallet to Bid'}
        </button>
      ) : (
        <button
          onClick={handleFeature}
          disabled={!meetsMinimum || !canAfford || featuring}
          className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
            meetsMinimum && canAfford
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90 shadow-lg shadow-amber-500/20'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          } disabled:opacity-50`}
        >
          {featuring ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Star className="w-4 h-4" />
          )}
          {featuring
            ? 'Placing Bid...'
            : bidSats > 0
              ? `Bid ${satsToUSD(bidSats, bchPrice) || `${bidSats.toLocaleString()} sats`}`
              : 'Enter a bid amount'
          }
        </button>
      )}
      {!canAfford && bidSats > 0 && tipWallet.ready && (
        <p className="text-[10px] text-red-500">Not enough funds. Deposit to your wallet in Settings.</p>
      )}
      {featureError && <p className="text-xs text-red-500 mt-1">{featureError}</p>}
    </div>
  );
}

// ── Create Campaign ──────────────────────────────────────────────
function CreateCampaign({ onBack, onCreated, bchPrice }: { onBack: () => void; onCreated: () => void; bchPrice: number | null }) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const { address: fetchedBCHAddress, loading: addressLoading } = useBCHAddress(user?.pubkey);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [goalUSD, setGoalUSD] = useState('');
  const [durationDays, setDurationDays] = useState(30);
  const [image, setImage] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const imageFileRef = useRef<HTMLInputElement>(null);
  const [manualAddress, setManualAddress] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use fetched address as default, allow manual override
  const effectiveAddress = manualAddress.trim() || fetchedBCHAddress || '';
  const hasValidAddress = effectiveAddress.startsWith('bitcoincash:') || effectiveAddress.startsWith('q') || effectiveAddress.startsWith('p');

  // Pre-fill manual address when fetched address arrives
  useEffect(() => {
    if (fetchedBCHAddress && !manualAddress) {
      setManualAddress(fetchedBCHAddress);
    }
  }, [fetchedBCHAddress]);

  const goalSats = usdToSats(parseFloat(goalUSD) || 0, bchPrice);

  const handleCreate = async () => {
    if (!user || !title.trim() || !goalUSD || !hasValidAddress) return;
    setCreating(true);
    setError(null);

    try {
      const now = Math.floor(Date.now() / 1000);
      const campaignId = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

      const bchAddr = effectiveAddress.startsWith('bitcoincash:') ? effectiveAddress : 'bitcoincash:' + effectiveAddress;
      const campaignData: CampaignData = {
        title: title.trim(),
        description: description.trim(),
        goalSats,
        deadline: now + durationDays * 86400,
        bchAddress: bchAddr,
        image: image.trim() || undefined,
        createdBy: user.pubkey,
      };

      await publish({
        kind: CAMPAIGN_KIND,
        content: JSON.stringify(campaignData),
        tags: [
          ['d', campaignDTag(campaignId)],
          ['t', CAMPAIGN_TAG],
          ['t', 'bch'],
          ['t', 'bitcoincash'],
          ['goal', String(goalSats)],
          ['deadline', String(campaignData.deadline)],
        ],
        created_at: now,
      });

      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        <div className="glass-card">
          <h2 className="font-bold text-base flex items-center gap-2 mb-4">
            <Plus className="w-5 h-5 text-primary" /> Create Campaign
          </h2>

          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Campaign Title *</label>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. BCH Meetup in Manila"
                className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                maxLength={100}
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Description</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="What's the campaign for? How will funds be used? Tell your story — the more detail, the better!"
                className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-y min-h-[120px]"
                rows={6}
                maxLength={5000}
              />
              <p className="text-[10px] text-muted-foreground mt-1 text-right">{description.length}/5,000</p>
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Funding Goal (USD) *</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={goalUSD}
                  onChange={e => setGoalUSD(e.target.value)}
                  placeholder="100"
                  className="w-full bg-muted rounded-lg pl-8 pr-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              {goalSats > 0 && (
                <p className="text-[10px] text-muted-foreground mt-1">= {goalSats.toLocaleString()} sats at current price</p>
              )}
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Duration</label>
              <select
                value={durationDays}
                onChange={e => setDurationDays(Number(e.target.value))}
                className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Cover Image (optional)</label>
              {image ? (
                <div className="relative mt-1">
                  <img
                    src={image}
                    alt="Cover preview"
                    className="w-full h-40 rounded-xl object-cover border border-border"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setImage('')}
                    className="absolute top-2 right-2 p-1 bg-black/60 rounded-full text-white hover:bg-black/80 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2 mt-1">
                  <input type="file" ref={imageFileRef} accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    e.target.value = '';
                    setImageUploading(true);
                    try {
                      const url = await uploadImage(file);
                      setImage(url);
                    } catch (err) {
                      setError('Image upload failed. Try a smaller image or paste a URL instead.');
                    } finally {
                      setImageUploading(false);
                    }
                  }} />
                  <button
                    type="button"
                    onClick={() => imageFileRef.current?.click()}
                    disabled={imageUploading}
                    className="flex-1 flex items-center justify-center gap-2 py-6 rounded-xl border-2 border-dashed border-border hover:border-primary/40 transition-colors cursor-pointer"
                  >
                    {imageUploading ? (
                      <><Loader2 className="w-5 h-5 animate-spin text-primary" /> <span className="text-xs text-muted-foreground">Uploading...</span></>
                    ) : (
                      <><Upload className="w-5 h-5 text-muted-foreground" /> <span className="text-xs text-muted-foreground">Upload Image</span></>
                    )}
                  </button>
                </div>
              )}
              {/* URL fallback */}
              {!image && !imageUploading && (
                <div className="mt-2">
                  <p className="text-[10px] text-muted-foreground mb-1">Or paste an image URL:</p>
                  <input
                    value=""
                    onChange={e => {
                      const url = e.target.value.trim();
                      if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                        setImage(url);
                      }
                    }}
                    onPaste={e => {
                      const url = e.clipboardData.getData('text').trim();
                      if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                        e.preventDefault();
                        setImage(url);
                      }
                    }}
                    placeholder="https://example.com/image.jpg"
                    className="w-full bg-muted rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Your BCH Address (receives pledges) *</label>
              <input
                value={manualAddress}
                onChange={e => setManualAddress(e.target.value)}
                placeholder={addressLoading ? 'Loading your address...' : 'bitcoincash:qz...'}
                className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {!hasValidAddress && manualAddress && (
                <p className="text-[10px] text-red-500 mt-1">Enter a valid BCH address (starts with bitcoincash: or q)</p>
              )}
              {!manualAddress && !addressLoading && !fetchedBCHAddress && (
                <p className="text-[10px] text-amber-500 mt-1">Paste your BCH address above. You can also set it in Settings for future use.</p>
              )}
            </div>

            <div className="rounded-lg bg-muted/50 p-3 text-[10px] text-muted-foreground">
              <p><strong className="text-foreground">How it works:</strong> Backers send BCH directly to your address. A 3% platform fee is deducted from each pledge. No refunds — backers trust your Nostr reputation.</p>
            </div>

            <button
              onClick={handleCreate}
              disabled={creating || !title.trim() || !goalUSD || !hasValidAddress}
              className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Target className="w-4 h-4" />}
              {creating ? 'Creating...' : 'Launch Campaign'}
            </button>
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Campaign QR Donate Card ───────────────────────────────────────
// Shown to everyone (no login required) so non-Nostr users can donate
// directly by scanning the BCH address QR code.
// Logged-in users can tap "I sent this" to register their QR donation
// as a Nostr pledge event so it counts toward the campaign total.
function CampaignQRCard({
  bchAddress,
  campaignTitle,
  bchPrice,
  goalSats,
  campaignId,
  campaignPubkey,
  onPledgeConfirmed,
}: {
  bchAddress: string;
  campaignTitle: string;
  bchPrice: number | null;
  goalSats: number;
  campaignId?: string;
  campaignPubkey?: string;
  onPledgeConfirmed?: () => void;
}) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const [copied, setCopied] = useState(false);
  const [imgError, setImgError] = useState(false);

  // "Confirm my donation" flow
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAmount, setConfirmAmount] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [confirmDone, setConfirmDone] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(bchAddress)}&ecc=M&margin=1`;

  const handleCopy = () => {
    navigator.clipboard.writeText(bchAddress).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = async () => {
    try {
      const res = await fetch(qrUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${campaignTitle.replace(/\s+/g, '-').toLowerCase()}-bch-qr.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  // Publish a pledge receipt so the QR donation is counted on Nostr
  const handleConfirmDonation = async () => {
    if (!user || !campaignId || !campaignPubkey) return;
    const usdVal = parseFloat(confirmAmount);
    if (isNaN(usdVal) || usdVal <= 0) {
      setConfirmError('Enter the amount you sent in USD');
      return;
    }
    const amountSats = bchPrice ? Math.round((usdVal / bchPrice) * 1e8) : 0;
    setConfirming(true);
    setConfirmError(null);
    try {
      await publish({
        kind: PLEDGE_KIND,
        content: `Donated ${confirmAmount ? `$${usdVal.toFixed(2)}` : ''} to "${campaignTitle}" via QR code #bch-crowdfund #bitcoincash`,
        tags: [
          ['e', campaignId],
          ['p', campaignPubkey],
          ['t', PLEDGE_TAG],
          ['t', 'bch'],
          ['t', 'bitcoincash'],
          ...(amountSats > 0 ? [['amount', String(amountSats)]] : []),
          ['method', 'qr'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      // Viral public note for QR donations too
      const campaignUrl = `https://bchnostr.com/crowdfund?campaign=${campaignId}`;
      const qrViralMessages = [
        `\uD83D\uDFE2 I just backed "${campaignTitle}" with Bitcoin Cash on BCHNostr! Every satoshi counts — join me! \uD83D\uDC47\n\n${campaignUrl}\n\n#BCH #BitcoinCash #bchnostr`,
        `💚 Just contributed to "${campaignTitle}" — check it out and back it with BCH!\n\n${campaignUrl}\n\n#BCH #BitcoinCash #bchnostr`,
        `\u26A1 Backed! Just sent BCH to support "${campaignTitle}" on BCHNostr 🚀\n\n${campaignUrl}\n\n#BCH #BitcoinCash #bchnostr`,
      ];
      const qrViralNote = qrViralMessages[Math.floor(Math.random() * qrViralMessages.length)];
      publish({
        kind: 1,
        content: qrViralNote,
        tags: [
          ['p', campaignPubkey],
          ['t', 'bch'],
          ['t', 'bitcoincash'],
          ['t', 'bchnostr'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      }).catch(() => {});

      setConfirmDone(true);
      setShowConfirm(false);
      onPledgeConfirmed?.();
    } catch (e) {
      setConfirmError(e instanceof Error ? e.message : 'Failed to record donation');
    } finally {
      setConfirming(false);
    }
  };

  const goalUsd = bchPrice && goalSats ? `$${((goalSats / 1e8) * bchPrice).toFixed(0)}` : null;

  return (
    <div className="glass-card">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <QrCode className="w-4 h-4 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-sm">Donate with BCH</h3>
          <p className="text-[10px] text-muted-foreground">No account needed — scan &amp; send directly</p>
        </div>
      </div>

      <div className="flex gap-4 items-start">
        {/* QR code */}
        <div className="shrink-0">
          {!imgError ? (
            <div className="w-[120px] h-[120px] rounded-xl border-2 border-border overflow-hidden bg-white p-1.5">
              <img
                src={qrUrl}
                alt={`BCH QR code for ${campaignTitle}`}
                className="w-full h-full object-contain"
                onError={() => setImgError(true)}
              />
            </div>
          ) : (
            <div className="w-[120px] h-[120px] rounded-xl border-2 border-border bg-muted flex items-center justify-center">
              <QrCode className="w-8 h-8 text-muted-foreground/40" />
            </div>
          )}
          <button
            onClick={handleDownload}
            className="mt-1.5 w-full flex items-center justify-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors"
          >
            <Download className="w-3 h-3" /> Save QR
          </button>
        </div>

        {/* Address + copy */}
        <div className="flex-1 min-w-0 space-y-2">
          <div>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">BCH Address</p>
            <div className="bg-muted rounded-lg p-2">
              <code className="text-[10px] font-mono break-all leading-relaxed text-foreground/80">
                {bchAddress}
              </code>
            </div>
          </div>

          <button
            onClick={handleCopy}
            className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-colors ${
              copied
                ? 'bg-emerald-500/15 text-emerald-500 border border-emerald-500/20'
                : 'bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20'
            }`}
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied!' : 'Copy Address'}
          </button>

          {goalUsd && (
            <p className="text-[10px] text-muted-foreground text-center">
              Goal: <span className="font-semibold text-foreground">{goalUsd}</span>
            </p>
          )}
        </div>
      </div>

      {/* "I already sent BCH" confirmation — for logged-in users */}
      {campaignId && (
        <div className="mt-3 pt-3 border-t border-border/60">
          {confirmDone ? (
            <div className="flex items-center gap-2 justify-center py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <Check className="w-4 h-4 text-emerald-500" />
              <p className="text-xs font-semibold text-emerald-500">Donation recorded! Thank you 💚</p>
            </div>
          ) : showConfirm ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-foreground">How much did you send?</p>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={confirmAmount}
                    onChange={e => { setConfirmAmount(e.target.value); setConfirmError(null); }}
                    placeholder="0.00"
                    className="w-full bg-muted rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    autoFocus
                  />
                </div>
                <button
                  onClick={handleConfirmDonation}
                  disabled={confirming || !confirmAmount}
                  className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1.5 shrink-0"
                >
                  {confirming
                    ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    : <Check className="w-3.5 h-3.5" />}
                  Confirm
                </button>
                <button
                  onClick={() => { setShowConfirm(false); setConfirmAmount(''); setConfirmError(null); }}
                  className="p-2 rounded-lg hover:bg-muted text-muted-foreground"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              {confirmError && <p className="text-[11px] text-red-500">{confirmError}</p>}
              <p className="text-[10px] text-muted-foreground">
                This records your donation on Nostr so it counts toward the campaign total.
              </p>
            </div>
          ) : (
            <button
              onClick={() => {
                if (!user) { setShowLoginDialog(true); return; }
                setShowConfirm(true);
              }}
              className="w-full py-2 rounded-lg border border-primary/30 text-primary text-xs font-semibold hover:bg-primary/5 transition-colors flex items-center justify-center gap-1.5"
            >
              <Heart className="w-3.5 h-3.5" />
              Already sent BCH? Tap to register your contribution
            </button>
          )}
        </div>
      )}

      {/* How-to footer */}
      <div className="mt-3 pt-3 border-t border-border/60 space-y-1">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">How to donate</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[
            { step: '1', text: 'Open any BCH wallet', emoji: '\uD83D\uDCF1' },
            { step: '2', text: 'Scan or paste address', emoji: '\uD83D\uDCF7' },
            { step: '3', text: 'Send any amount', emoji: '💚' },
          ].map(s => (
            <div key={s.step} className="bg-muted/50 rounded-lg py-2 px-1">
              <p className="text-base mb-0.5">{s.emoji}</p>
              <p className="text-[9px] text-muted-foreground leading-tight">{s.text}</p>
            </div>
          ))}
        </div>
        <a
          href="https://bchnostr.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 mt-1.5 text-[10px] text-primary hover:underline"
        >
          <ExternalLink className="w-2.5 h-2.5" />
          Join BCHNostr to back campaigns &amp; earn BCH tips
        </a>
      </div>
    </div>
  );
}

